import * as React from 'react';
import {
  Bloodwork,
  BloodworkAnalysis,
  BloodworkRange,
  FurtherConsiderations,
  MenstrualPhase,
  PanelField,
  PanelMarker,
} from '../../models';
import { FurtherConsiderationsEntryStore } from '../../stores/further-considerations-entry-store';
import { DisclaimerSection } from '../bloodworkz/disclaimer-section';
import { Card, Table, OverlayTrigger, Tooltip, Popover, Button, Image } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faFileDownload, faFileCode } from '@fortawesome/pro-regular-svg-icons';
import { MathUtils } from '../../utils/MathUtils';
import Moment from 'react-moment';
import moment from 'moment';
import { UserProfile } from '../../models/UserProfile';
import { UserProfileUIStore } from '../../stores/user-profile-ui-store';
import Storage from '@aws-amplify/storage';
import { PDFModal } from '../common/pdf-modal';
import { GenericModal } from '../common/generic-modal';
import { buildAIExportData } from '../../helpers/AIExportBuilder';
import { AI_PROGRESS_SUMMARY_PROMPT } from '../../helpers/aiReviewPrompt';
import { BloodworkEntryStore } from '../../stores/bloodwork-entry-store';
import { ClientEntryStore } from '../../stores/client-entry-store';
import { ClientNotes } from './client-notes';
import {
  renderNoteLine,
  MarkdownToolbar,
  NOTE_TEXT_COLOR,
  wrapSelection,
  formatMarkerForKeydown,
} from './note-markdown';
import { SectionNotesSummaryPanel } from './section-notes-summary-panel';
import { scrollPanelIntoView } from '../../utils/scrollPanelIntoView';
import { isSameBloodwork } from '../../utils/sameBloodwork';
import { Section } from '../../models';
import { faFileDownload as faFileDownloadSolid, faInfoCircle } from '@fortawesome/pro-solid-svg-icons';

const arrow_up = require('../../assets/imgs/long-arrow-up-8x13.png');
const arrow_down = require('../../assets/imgs/long-arrow-down-8x12.png');
const thumbs_up = require('../../assets/imgs/thumbs-up-15x15.png');
const thumbs_down = require('../../assets/imgs/thumbs-down-15x15.png');
const logo = require('../../assets/imgs/logo.png');

const NUM_RESULTS_TO_COMPARE: number = 10;
const NUM_RESULTS_TO_COMPARE_FOR_PDF: number = 5;

// Breathing room above a panel that starts a PDF page (the Insights table and
// Overall Progress & Trends chunks each open with a forced page break, so this
// sits at the very top of the page). Matches the Disclaimer wrapper's
// padding-top so all three panels clear the page edge by the same amount.
// Counted in the chunkers' HEADER_HEIGHT — raising it here without raising
// that would let a chunk run past the bottom of the page.
const TOP_OF_PAGE_SPACER = '24px';

// Short prompt shown in the "Export Raw Data" info popover for practitioners to
// copy and use with the exported file in an AI assistant.
const AI_FILE_PROMPT =
  'You are a functional-medicine analyst. Using ONLY the attached LabSmarts blood ' +
  'work export, write a BRIEF practitioner summary — synthesize what matters and ' +
  'what changed; do NOT re-list values (the practitioner already has them in the ' +
  'report and the Markers-Outside-Optimal summary). Cover: a one-line snapshot; ' +
  '3–5 top findings that group related markers into one insight (drawn from the ' +
  'insight panels, the 5-system Results Overview scores/colors, and section notes — ' +
  'cover every flagged body system, including ones without detailed patterns); ' +
  'notable changes across tests; and any brief follow-ups. Decision-support, not a diagnosis.';

/** Button that copies the given text to the clipboard with brief "Copied!" feedback. */
const CopyPromptButton: React.FC<{ text: string }> = ({ text }) => {
  const [copied, setCopied] = React.useState(false);
  const onCopy = () => {
    const done = () => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    };
    if (navigator.clipboard && navigator.clipboard.writeText) {
      navigator.clipboard.writeText(text).then(done).catch(() => undefined);
    }
  };
  return (
    <Button
      size="sm"
      variant="outline-secondary"
      onClick={onCopy}
      style={{ marginTop: '8px' }}
    >
      {copied ? 'Copied!' : 'Copy prompt'}
    </Button>
  );
};

interface Props {
  bloodworks: Bloodwork[];
  bloodworkRanges: BloodworkRange[];
  currentUserProfile: UserProfile;
  onExportToPdf: Function;
  onHidePdfModal: Function;
  bloodworkEntryStore: BloodworkEntryStore;
  clientEntryStore: ClientEntryStore;
  furtherConsiderationsEntryStore?: FurtherConsiderationsEntryStore;
  userProfileUIStore?: UserProfileUIStore;
}

type State = {
  showPdfModal: boolean;
  showUpgradeModal: boolean;
  numResultsToCompare: number;
  headerLogoSrc?: string; // resolved preview URL for the modal's <Image id="logo">
  showHiddenRows: boolean; // toggle to show/hide rows with no value
  matchAnalysisOrder: boolean; // toggle section order: true = test-results analysis order, false = legacy lab-report order
  withNotes: boolean; // toggle to include practitioner notes in PDF (changed name to Insights)
  showSectionNotes: boolean; // toggle the Section Notes column in the notes table (screen + PDF)
  furtherConsiderations: FurtherConsiderations;
  generatingSummary: boolean; // AI "Overall Progress & Trends" request in flight
  aiSummaryDraft: string; // latest AI-generated draft to inject into the notes field
};

// What the comparison report shows for one collection date. The Insights note
// (Section.NOTES) is only half of it: a practitioner can type everything in the
// per-section Notes tabs and never touch Insights, and those notes have to come
// across too — otherwise that test looks like it has no notes at all.
function insightsTextFor(bloodwork: Bloodwork): string {
  return (bloodwork?.bloodworkNotes?.findNote(Section.NOTES)?.text || '').trim();
}

// That test's consolidated Section Notes — the practitioner's edited override
// when there is one, otherwise each section's own note under its "## Label".
function sectionNotesTextFor(bloodworkAnalysis: BloodworkAnalysis): string {
  return SectionNotesSummaryPanel.bodyText(bloodworkAnalysis).trim();
}

// Insights first, then the section notes beneath it. Either half on its own is
// enough to give the date a row.
export function comparisonNotesTextFor(
  bloodworkAnalysis: BloodworkAnalysis,
): string {
  return [
    insightsTextFor(bloodworkAnalysis?.bloodwork),
    sectionNotesTextFor(bloodworkAnalysis),
  ]
    .filter(Boolean)
    .join('\n\n');
}

// One collection date's notes as the PDF renders them: each column already
// sliced to what fits on this page. isCont marks the continuation halves.
export type NoteChunkRow = {
  collectionDate: string;
  insightsSplit: string;
  sectionSplit: string;
  isCont: boolean;
};

/**
 * Pre-paginate the notes table for the PDF: an array of page chunks, each an
 * array of rows. html2pdf can't split a row (and the date cell can't be
 * rowSpanned across a break), so both columns are sliced against the SAME page
 * budget — a row that spills carries both halves onto the next page in step,
 * otherwise one column would run ahead and pair the wrong text with the wrong
 * date. Row height is the taller column, not the sum of the two.
 */
export function paginateNoteRows(
  entries: {
    collectionDate: string;
    insights: string;
    sectionNotes: string;
  }[],
  opts: { showSectionNotes: boolean },
): NoteChunkRow[][] {
  // PAGE GEOMETRY, measured off a real export rather than guessed. html2pdf
  // renders into a container whose CSS width is pageSize.inner.width in mm
  // (worker.js:110) and its jsPDF format defaults to a4 — so landscape a4
  // (297x210mm) less the [3,5,10,5] margins set in PDFModal gives a 287x197mm
  // inner box: ~1085 CSS px wide, ~744 CSS px tall per page.
  // All the safety margin lives HERE, in the page budget — never in WRAP_CHARS
  // below. A too-small divisor is wrong once per line and compounds: at 74
  // instead of 82, a 163-character bullet scores 3 lines instead of 2, and 8
  // such bullets lose a third of a page. 44px of reserve absorbs a couple of
  // under-estimated lines instead.
  const MAX_PAGE_HEIGHT = 700;
  // Top spacer (24px, see TOP_OF_PAGE_SPACER) + card header + table header.
  const HEADER_HEIGHT = 76;
  const ROW_PADDING = 12; // Internal padding per row
  const LINE_HEIGHT = 18; // Height per line; measured ~17.7, so it leans safe
  // Characters on one rendered line, per column — the REAL figure, no cushion:
  // the column's share of the ~1083px table, less 24px of cell padding, over
  // the ~5.6px average character width. 45% -> 82, 40% -> 73, 80% -> 150.
  const INS_WRAP = opts.showSectionNotes ? 82 : 150;
  const SEC_WRAP = 73;

  // Take as many lines as fit in `budget` and hand back the rest for the next
  // page. forceOne stops a single over-tall line from stalling a fresh page.
  const takeLines = (
    lines: string[],
    budget: number,
    forceOne: boolean,
    wrapChars: number,
  ) => {
    const taken: string[] = [];
    let height = 0;
    let i = 0;
    while (i < lines.length) {
      const h =
        Math.max(1, Math.ceil(lines[i].length / wrapChars)) * LINE_HEIGHT;
      if (height + h > budget && (taken.length > 0 || !forceOne)) break;
      taken.push(lines[i]);
      height += h;
      i++;
    }
    return { taken, rest: lines.slice(i), height };
  };

  const noteChunks: NoteChunkRow[][] = [];
  let currentChunk: NoteChunkRow[] = [];
  let currentHeight = HEADER_HEIGHT;

  entries.forEach(entry => {
    let insLines = entry.insights ? entry.insights.split('\n') : [];
    let secLines =
      opts.showSectionNotes && entry.sectionNotes
        ? entry.sectionNotes.split('\n')
        : [];
    if (insLines.length === 0 && secLines.length === 0) return;

    // If the current chunk is too full to fit even two more lines, flush it
    // before starting a new note so the next collection date starts on a fresh page.
    if (
      currentChunk.length > 0 &&
      currentHeight + LINE_HEIGHT * 2 > MAX_PAGE_HEIGHT
    ) {
      noteChunks.push(currentChunk);
      currentChunk = [];
      currentHeight = HEADER_HEIGHT;
    }

    let partsAdded = 0;
    while (true) {
      const budget = MAX_PAGE_HEIGHT - currentHeight;
      const fresh = currentChunk.length === 0;
      const ins = takeLines(insLines, budget, fresh, INS_WRAP);
      const sec = takeLines(secLines, budget, fresh, SEC_WRAP);

      if (ins.taken.length === 0 && sec.taken.length === 0) {
        // Nothing fits in what's left of this page — start a fresh one. (Can't
        // loop forever: on a fresh chunk `forceOne` always takes a line.)
        noteChunks.push(currentChunk);
        currentChunk = [];
        currentHeight = HEADER_HEIGHT;
        continue;
      }

      currentChunk.push({
        collectionDate: entry.collectionDate,
        insightsSplit: ins.taken.join('\n'),
        sectionSplit: sec.taken.join('\n'),
        isCont: partsAdded > 0,
      });
      currentHeight += Math.max(ins.height, sec.height) + ROW_PADDING;
      insLines = ins.rest;
      secLines = sec.rest;
      if (insLines.length === 0 && secLines.length === 0) break;

      noteChunks.push(currentChunk);
      currentChunk = [];
      currentHeight = HEADER_HEIGHT;
      partsAdded++;
    }
  });

  if (currentChunk.length > 0) {
    noteChunks.push(currentChunk);
  }

  return noteChunks;
}

class PractitionerNoteRow extends React.Component<
  {
    bloodwork: Bloodwork;
    bloodworkEntryStore: BloodworkEntryStore;
    // Read-only here — Edit on this row writes the Insights note only.
    sectionNotesText?: string;
    showSectionNotesColumn?: boolean;
  },
  { isEditing: boolean; text: string }
> {
  constructor(props: any) {
    super(props);
    const note = props.bloodwork.bloodworkNotes?.findNote(Section.NOTES);
    this.state = {
      isEditing: false,
      text: note ? note.text : '',
    };
  }

  textareaRef = React.createRef<HTMLTextAreaElement>();
  // This note's own row — where Save scrolls back to.
  rowRef = React.createRef<HTMLTableRowElement>();

  componentDidUpdate(prevProps: any, prevState: any) {
    if (
      this.state.isEditing &&
      !prevState.isEditing &&
      this.textareaRef.current
    ) {
      this.textareaRef.current.style.height = 'auto';
      this.textareaRef.current.style.height = `${this.textareaRef.current.scrollHeight}px`;
    }
  }
  componentWillReceiveProps(nextProps: any) {
    // A row pointed at a DIFFERENT test reseeds even mid-edit: what was typed
    // belongs to the test that was there, and Save would write it over this
    // one's note. Rows are keyed by test id, so this is the safety net rather
    // than the normal path.
    const switchedTest = !isSameBloodwork(
      nextProps.bloodwork,
      this.props.bloodwork,
    );
    if (!this.state.isEditing || switchedTest) {
      const note = nextProps.bloodwork.bloodworkNotes?.findNote(Section.NOTES);
      this.setState({
        isEditing: switchedTest ? false : this.state.isEditing,
        text: note ? note.text : '',
      });
    }
  }
  handleEdit = () => this.setState({ isEditing: true });

  // Same Bold/Italic/Underline tools as the Insights panel this note is typed
  // in — it's the same note (Section.NOTES), just shown per collection date.
  applyFormat = (value: string, selStart: number, selEnd: number) => {
    this.setState({ text: value }, () => {
      const el = this.textareaRef.current;
      if (!el) return;
      el.style.height = 'auto';
      el.style.height = `${el.scrollHeight}px`;
      requestAnimationFrame(() => {
        el.focus();
        el.setSelectionRange(selStart, selEnd);
      });
    });
  };

  handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    const marker = formatMarkerForKeydown(e);
    if (marker && this.textareaRef.current) {
      e.preventDefault();
      const { value, selStart, selEnd } = wrapSelection(
        this.textareaRef.current,
        marker,
      );
      this.applyFormat(value, selStart, selEnd);
    }
  };

  handleSave = (e: any) => {
    e.preventDefault();
    const { bloodwork, bloodworkEntryStore } = this.props;
    let note = bloodwork.bloodworkNotes?.findNote(Section.NOTES);
    if (!note) {
      note = bloodwork.bloodworkNotes?.addNote(Section.NOTES, '');
    }
    if (note) note.text = this.state.text;
    // Scroll back to this row once the save resolves — the spinner modal locks
    // body scrolling while it's up, so without this the page lands at the top
    // of the report.
    const scrollBack = () => scrollPanelIntoView(this.rowRef.current);
    bloodworkEntryStore.saveBloodwork(bloodwork)
      .then(scrollBack)
      .catch((err) => {
        console.error('Failed to save bloodwork note', err);
        alert('Failed to save your note. Please try again.');
        scrollBack();
      });
    this.setState({ isEditing: false });
  };
  handleCancel = () => {
    const note = this.props.bloodwork.bloodworkNotes?.findNote(Section.NOTES);
    this.setState({ isEditing: false, text: note ? note.text : '' });
  };
  render() {
    const { bloodwork, sectionNotesText, showSectionNotesColumn } = this.props;
    const { isEditing, text } = this.state;
    return (
      <tr ref={this.rowRef}>
        <td>
          <Moment format="MM/DD/YYYY">{bloodwork.collectionDate}</Moment>
        </td>
        <td>
          {isEditing ? (
            <>
              <MarkdownToolbar
                getTextarea={() => this.textareaRef.current}
                onApply={this.applyFormat}
              />
              <textarea
                ref={this.textareaRef}
                value={text}
                onChange={e => {
                  this.setState({ text: e.target.value });
                  e.target.style.height = 'auto';
                  e.target.style.height = `${e.target.scrollHeight}px`;
                }}
                onKeyDown={this.handleKeyDown}
                style={{
                  width: '100%',
                  minHeight: '60px',
                  overflow: 'hidden',
                  resize: 'none',
                  // A raw textarea keeps the browser's own font, so it read
                  // bigger (and in a different face) than the formatted note it
                  // turns into. Match the page.
                  fontFamily: 'inherit',
                  fontSize: '12px',
                }}
              />
              <div style={{ fontSize: '12px', color: '#6c757d', marginTop: 4 }}>
                Formatting:&nbsp;<code>## Section</code>&nbsp;for section
                headings,&nbsp;<code>**bold**</code>,&nbsp;<code>*italic*</code>
                ,&nbsp;<code>_underline_</code>. These render formatted when you
                Save.
              </div>
            </>
          ) : (
            // Formatted, not raw markup — a note typed with **bold** in the
            // Insights panel has to read the same here. Line by line, because
            // the PDF build of this table paginates it that way.
            <div
              style={{
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                minHeight: '1.5em',
                color: NOTE_TEXT_COLOR,
              }}
            >
              {(text || '').trim() ? (
                (text || '').split('\n').map((line, lIdx) => (
                  <div key={lIdx}>{renderNoteLine(line)}</div>
                ))
              ) : (
                // A test can have section notes and no Insights note — say so
                // rather than leaving a cell that reads as broken.
                <span style={{ color: '#adb5bd' }}>&mdash;</span>
              )}
            </div>
          )}
        </td>
        {/* Section Notes ride in their own column so they never read as part of
            the Insights note, which summarises the whole test. Read-only here —
            Edit on this row writes the Insights note only. */}
        {showSectionNotesColumn && (
          <td>
            {(sectionNotesText || '').trim() ? (
              <div
                style={{
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  color: NOTE_TEXT_COLOR,
                }}
              >
                {sectionNotesText!.split('\n').map((line, lIdx) => (
                  <div key={`sn-${lIdx}`}>{renderNoteLine(line)}</div>
                ))}
              </div>
            ) : (
              <span style={{ color: '#adb5bd' }}>&mdash;</span>
            )}
          </td>
        )}
        <td style={{ textAlign: 'center' }}>
          {isEditing ? (
            <>
              <button
                className="btn btn-sm btn-primary"
                onClick={this.handleSave}
                style={{ marginRight: '4px' }}
              >
                Save
              </button>
              <button
                className="btn btn-sm btn-secondary"
                onClick={this.handleCancel}
              >
                Cancel
              </button>
            </>
          ) : (
            <button
              className="btn btn-sm btn-primary"
              onClick={this.handleEdit}
            >
              Edit
            </button>
          )}
        </td>
      </tr>
    );
  }
}

// Max compact Hormone Balance rows that fit beneath the section heading +
// column header on one landscape PDF page. The legacy layout packs up to 19
// compact hormone rows *plus* the Prostate panel (3 rows) onto a single page,
// so ~22 compact hormone rows alone — with even less overhead (one title, one
// column header) — clear a page without being sliced mid-row by html2pdf.
// With Cortisol AM/PM, DHEA-S and Pregnenolone moved out to Stress & Adrenal,
// the full hormone set is at most 22 rows (menstrual phase + 15 hormones + the
// 2 BioT + 2 FreeT + 2 TotalT markers) when "Hide blank rows" is unchecked, so
// the threshold is raised to 24 to keep the whole table — including the two
// FreeT and two TotalT markers — on ONE page instead of spilling them onto a
// second chunk. Drives both the page-chunking of the table
// (renderHormoneSectionPdf) and the "pack Prostate on the same page" rule.
const HORMONE_ROWS_PER_PDF_PAGE = 24;

export class BloodworkComparisonAnalysis extends React.Component<Props, State> {
  // Anchors the "Blood Work Comparison" card so we can restore scroll to its
  // top after a toggle re-orders/re-renders the sections (otherwise the page
  // jumps mid-table to a section like "Blood Oxygen & Red Blood Cell Function").
  private comparisonCardRef = React.createRef<HTMLDivElement>();

  /**
   * Scroll the comparison card title back to the top of the viewport, offset by
   * the sticky header so the title isn't hidden beneath it. Mirrors the
   * sticky-offset pattern in scrollToComparison (bloodwork-analysis-container).
   */
  private scrollComparisonTitleIntoView = () => {
    // Defer to the next frame so the measurement happens after the table has
    // re-ordered/re-rendered and the browser has settled layout — otherwise
    // toggles that trigger extra renders (e.g. Match Analysis Order's async
    // profile save) scroll to a stale position and land inconsistently.
    requestAnimationFrame(() => {
      const element = this.comparisonCardRef.current;
      if (!element) return;
      const sticky = document.querySelector(
        '.App-sticky-top',
      ) as HTMLElement | null;
      const stickyHeight = sticky ? sticky.getBoundingClientRect().height : 0;
      const top =
        element.getBoundingClientRect().top + window.scrollY - stickyHeight;
      window.scrollTo({ top });
    });
  };

  state: State = {
    showPdfModal: false,
    showUpgradeModal: false,
    numResultsToCompare: NUM_RESULTS_TO_COMPARE,
    headerLogoSrc: undefined,
    showHiddenRows: false, // default: hide empty rows
    matchAnalysisOrder: true, // default: order sections like the test-results analysis
    withNotes: false,
    showSectionNotes: true, // default: show the Section Notes column
    furtherConsiderations: new FurtherConsiderations(),
    generatingSummary: false,
    aiSummaryDraft: '',
  };

  /**
   * Build the raw-data payload, send it to the AI (Bedrock) via the client
   * endpoint, and drop the returned summary into the "Overall Progress & Trends"
   * field as an editable draft (the practitioner reviews, then Saves).
   */
  onGenerateSummary = async () => {
    const { bloodworks, bloodworkRanges, clientEntryStore } = this.props;
    const { furtherConsiderations } = this.state;
    if (this.state.generatingSummary) return;
    this.setState({ generatingSummary: true });
    try {
      const payload = buildAIExportData(
        bloodworks,
        bloodworkRanges,
        furtherConsiderations,
      );
      // This lands in a client-facing report field, so use the plain-language,
      // non-diagnostic prompt (not the practitioner one embedded in the export).
      const summary = await clientEntryStore.generateAiSummary({
        instructions: AI_PROGRESS_SUMMARY_PROMPT,
        analysisHistory: payload.analysisHistory,
      });
      this.setState({ aiSummaryDraft: summary || '' });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('AI summary generation failed', err);
      const status =
        (err && err.response && err.response.status) ||
        (err && err.message) ||
        'unknown error';
      // eslint-disable-next-line no-alert
      alert(`Could not generate the summary (${status}). Please try again.`);
    } finally {
      this.setState({ generatingSummary: false });
    }
  };

  // ----------- Helper to Hide Rows with No Values ---------
  /** Returns true if this marker has a value in at least one result in the history.
   *  Special-case menstrualPhase to consider altValue as a "value".
   */
  private fieldHasAnyValue(
    field: PanelField,
    history: BloodworkAnalysis[],
  ): boolean {
    return history.some(bwa => {
      const pm = this.findPanelMarker(field, bwa);
      if (!pm) return false;

      if (field.id === 'menstrualPhase') {
        const alt = pm.altValue;

        // Treat undefined, null, or empty/whitespace string as "no value"
        // So menstrual phase behaves like other markers when hide blank rows is ON.
        return alt !== undefined && alt !== null && String(alt).trim() !== '';
      }

      return typeof pm.hasValue === 'function' && pm.hasValue();
    });
  }

  /** Returns true if any field in the section has a value in the entire history */
  private sectionHasAnyValues(
    fields: PanelField[],
    history: BloodworkAnalysis[],
  ): boolean {
    return fields.some(f => this.fieldHasAnyValue(f, history));
  }

  // ----------- Section ordering ---------
  /**
   * Section keys in the order they should render.
   * - matchAnalysisOrder (default): mirrors the test-results analysis UI order.
   * - legacy: the historical lab-report order (CBC markers, then CMP, then lipids, etc.).
   */
  private comparisonSectionOrder(): string[] {
    return this.state.matchAnalysisOrder
      ? [
          'rbc',
          'iron',
          'glucose',
          'lipids',
          'thyroid',
          'nutrients',
          'proteins',
          'immune',
          'inflammation',
          'platelets',
          'liver',
          'kidney',
          'electrolytes',
          'stressAdrenal',
          'hormones',
          'prostate',
        ]
      : [
          'rbc',
          'iron',
          'immune',
          'platelets',
          'glucose',
          'kidney',
          'proteins',
          'nutrients',
          'electrolytes',
          'lipids',
          'inflammation',
          'liver',
          'thyroid',
          'stressAdrenal',
          'hormones',
          'prostate',
        ];
  }

  /**
   * Builds the Hormone Balance section (title + table), honoring "Hide blank rows"
   * and the menstrual-phase inclusion rules. Returns null when it should be hidden.
   */
  private buildHormoneSection(
    history: BloodworkAnalysis[],
    gender: any,
  ): { title: string; table: React.ReactNode } | null {
    // cortisol AM/PM, DHEA-S and pregnenolone moved to the Stress & Adrenal section.
    const hormoneFieldsBase = [
      ...PanelField.HORMONE_FIELDS_1,
      ...PanelField.HORMONE_FIELDS_2,
    ].filter(f => f.id !== 'menstrualPhase' && !['cortisolAM', 'cortisolPM', 'dheaS', 'pregnenolone'].includes(f.id));

    const hideBlanks = !this.state.showHiddenRows;

    const anyHormoneHasValue = hormoneFieldsBase.some(f =>
      this.fieldHasAnyValue(f, history),
    );
    if (!anyHormoneHasValue && hideBlanks) return null;

    const menstrualPhaseField = PanelField.ALL_FIELDS.find(
      f => f.id === 'menstrualPhase',
    );
    const hormoneFieldsForTable = [
      ...(menstrualPhaseField ? [menstrualPhaseField] : []),
      ...hormoneFieldsBase,
    ].filter((f, i, a) => a.findIndex(g => g.id === f.id) === i);

    const table = this.comparisonTable(history, hormoneFieldsForTable, gender);
    return table ? { title: 'Hormone Balance', table } : null;
  }

  /**
   * Renders the Hormone Balance section for the PDF, splitting it across
   * multiple pages when the visible markers exceed one page
   * (HORMONE_ROWS_PER_PDF_PAGE). Each spillover page repeats the column header
   * and carries a "Hormone Balance (cont.)" heading + a page break, mirroring
   * the "Insights by Collection Date (cont.)" / MOO "(cont.)" pattern. Without
   * the chunking, the full ~29-marker table is taller than a landscape page and
   * html2pdf slices it mid-row (the reported "cut off onto the next page" bug).
   * Returns null when the section should be hidden.
   *
   * @param history     bloodwork history (one column per collection date)
   * @param gender      client gender; menstrual phase is hidden for males
   * @param breakBefore force a page break before the first chunk
   */
  private renderHormoneSectionPdf(
    history: BloodworkAnalysis[],
    gender: any,
    breakBefore: boolean,
  ): React.ReactNode {
    const hideBlanks = !this.state.showHiddenRows;

    // cortisol AM/PM, DHEA-S and pregnenolone moved to the Stress & Adrenal section.
    const hormoneFieldsBase = [
      ...PanelField.HORMONE_FIELDS_1,
      ...PanelField.HORMONE_FIELDS_2,
    ].filter(f => f.id !== 'menstrualPhase' && !['cortisolAM', 'cortisolPM', 'dheaS', 'pregnenolone'].includes(f.id));

    const anyHormoneHasValue = hormoneFieldsBase.some(f =>
      this.fieldHasAnyValue(f, history),
    );
    if (!anyHormoneHasValue && hideBlanks) return null;

    const menstrualPhaseField = PanelField.ALL_FIELDS.find(
      f => f.id === 'menstrualPhase',
    );
    const hormoneFieldsForTable = [
      ...(menstrualPhaseField ? [menstrualPhaseField] : []),
      ...hormoneFieldsBase,
    ].filter((f, i, a) => a.findIndex(g => g.id === f.id) === i);

    // Mirror comparisonTable's row-visibility filter so the chunk boundaries
    // line up exactly with the rows that will actually render.
    const visibleFields = hormoneFieldsForTable.filter(field => {
      if (field.isHiddenFromComparison) return false;
      if (field.id === 'menstrualPhase' && gender === 'M') return false;
      const hasAny = this.fieldHasAnyValue(field, history);
      return hideBlanks ? hasAny : true;
    });

    if (visibleFields.length === 0) return null;

    const chunks: PanelField[][] = [];
    for (
      let i = 0;
      i < visibleFields.length;
      i += HORMONE_ROWS_PER_PDF_PAGE
    ) {
      chunks.push(visibleFields.slice(i, i + HORMONE_ROWS_PER_PDF_PAGE));
    }

    return (
      <>
        {chunks.map((chunkFields, chunkIdx) => {
          const table = this.comparisonTable(history, chunkFields, gender);
          if (!table) return null;
          const isCont = chunkIdx > 0;
          return (
            <React.Fragment key={`hormone-chunk-${chunkIdx}`}>
              {(breakBefore || isCont) && (
                <div className="html2pdf__page-break" />
              )}
              <div style={{ height: '8px' }} />
              <h5>
                <strong>Hormone Balance{isCont ? ' (cont.)' : ''}</strong>
              </h5>
              <div
                className="hormone-pdf-section"
                style={{
                  marginBottom: 0,
                  paddingBottom: 10,
                  pageBreakInside: 'avoid',
                  breakInside: 'avoid',
                }}
              >
                <style>
                  {`
                          .hormone-pdf-section td,
                          .hormone-pdf-section th {
                            padding-top: 3px !important;
                            padding-bottom: 3px !important;
                          }
                        `}
                </style>
                {table}
              </div>
            </React.Fragment>
          );
        })}
      </>
    );
  }

  /**
   * Resolves the comparison sections (title + table) in the currently selected
   * order, dropping any section that has no table / is hidden by "Hide blank rows".
   * Shared by the on-screen UI (both orders) and the PDF (analysis order only).
   */
  private buildComparisonSections(
    history: BloodworkAnalysis[],
    gender: any,
    whiteBloodCellsHasValues: boolean,
  ): { key: string; title: string; table: React.ReactNode }[] {
    const hideBlanks = !this.state.showHiddenRows;

    const make = (title: string, table: React.ReactNode | null) =>
      table ? { title, table } : null;

    const sections: {
      [key: string]: { title: string; table: React.ReactNode } | null;
    } = {
      rbc: make(
        'Blood Oxygen & Red Blood Cell Function',
        this.comparisonTable(history, [...PanelField.RBC_FIELDS]),
      ),
      iron:
        hideBlanks && !this.anyIronCoreMarkerHasValue(history)
          ? null
          : make(
              'Iron Status & Utilization',
              this.comparisonTable(history, [
                ...PanelField.IRON_FIELDS,
                ...PanelField.COPPER_FIELDS,
              ]),
            ),
      immune:
        !whiteBloodCellsHasValues && hideBlanks
          ? null
          : make(
              'Immune Activity',
              this.comparisonTable(history, [
                ...PanelField.IMMUNE_SYSTEM_FIELDS,
                ...PanelField.WBC_ABSOLUTE_DIFFERENTIALS,
              ]),
            ),
      platelets: make(
        'Blood Clotting Function',
        this.comparisonTable(history, this.plateletsComparisonFields(history)),
      ),
      glucose: make(
        'Glucose Regulation & Metabolic Function',
        this.comparisonTable(history, [
          ...PanelField.GLUCOSE_FIELDS,
          ...PanelField.GLUCOSE_TG_HDL_SECTION_FIELDS,
        ]),
      ),
      kidney: make(
        'Kidney Function',
        this.comparisonTable(history, [...PanelField.KIDNEY_FIELDS]),
      ),
      proteins: make(
        'Protein Status',
        this.comparisonTable(history, [...PanelField.PROTEINS_FIELDS]),
      ),
      nutrients: make(
        'Vitamin & Mineral Status',
        this.comparisonTable(history, [
          ...PanelField.NUTRIENTS_FIELDS,
          ...PanelField.ZINC_FIELDS,
        ]),
      ),
      electrolytes: make(
        'Electrolyte Balance',
        // Na/K ratio moved to the Stress & Adrenal section.
        this.comparisonTable(history, PanelField.ELECTROLYTES_FIELDS.filter(f => f.id !== 'sodiumPotassiumRatio')),
      ),
      lipids: make(
        'Lipid Balance',
        this.comparisonTable(history, [...PanelField.COMPARISON_LIPIDS_FIELDS]),
      ),
      inflammation: make(
        'Inflammation & Oxidative Stress',
        this.comparisonTable(history, [
          ...PanelField.COMPARISON_INFLAMMATION_FIELDS,
        ]),
      ),
      liver: make(
        'Liver Function',
        this.comparisonTable(history, [...PanelField.LIVER_FIELDS]),
      ),
      thyroid: make(
        'Thyroid Function',
        this.comparisonTable(history, [...PanelField.THYROID_FIELDS]),
      ),
      stressAdrenal: make(
        'Stress & Adrenal Balance',
        this.comparisonTable(history, [...PanelField.STRESS_ADRENAL_FIELDS]),
      ),
      hormones: this.buildHormoneSection(history, gender),
      prostate: make(
        'Prostate Function',
        this.comparisonTable(history, [...PanelField.PROSTATE_FIELDS]),
      ),
    };

    return this.comparisonSectionOrder()
      .map(key => {
        const s = sections[key];
        return s ? { key, title: s.title, table: s.table } : null;
      })
      .filter(Boolean) as {
      key: string;
      title: string;
      table: React.ReactNode;
    }[];
  }

  /**
   * Blood Clotting Function comparison field list. Always the coagulation
   * markers; the two viscosity inputs (Hematocrit, then Total Protein) are
   * appended ONLY when the viscosity estimate is present in the history — which
   * requires both inputs in the same test — so a lone HCT/protein row never
   * shows up under coagulation. Shared by the screen table, the PDF, and the
   * PDF height estimate so all three agree.
   */
  private plateletsComparisonFields(
    history: BloodworkAnalysis[],
  ): PanelField[] {
    const viscosity = PanelField.findField(
      PanelField.PLATELETS_FIELDS,
      'viscosityCapiliaries',
    );
    const showInputs =
      !!viscosity && this.fieldHasAnyValue(viscosity, history);
    return PanelField.plateletsComparisonFields(showInputs);
  }

  /**
   * The PanelField list backing each comparison section key, mirroring the
   * field sets used in buildComparisonSections. Used to estimate a section's
   * rendered height (via countVisibleFields) so the PDF renderer can decide
   * whether a section is tall enough to need its own fresh page.
   */
  private fieldsForComparisonKey(
    key: string,
    history: BloodworkAnalysis[],
  ): PanelField[] {
    switch (key) {
      case 'rbc':
        return [...PanelField.RBC_FIELDS];
      case 'iron':
        return [...PanelField.IRON_FIELDS, ...PanelField.COPPER_FIELDS];
      case 'immune':
        return [
          ...PanelField.IMMUNE_SYSTEM_FIELDS,
          ...PanelField.WBC_ABSOLUTE_DIFFERENTIALS,
        ];
      case 'platelets':
        return this.plateletsComparisonFields(history);
      case 'glucose':
        return [
          ...PanelField.GLUCOSE_FIELDS,
          ...PanelField.GLUCOSE_TG_HDL_SECTION_FIELDS,
        ];
      case 'kidney':
        return [...PanelField.KIDNEY_FIELDS];
      case 'proteins':
        return [...PanelField.PROTEINS_FIELDS];
      case 'nutrients':
        return [...PanelField.NUTRIENTS_FIELDS, ...PanelField.ZINC_FIELDS];
      case 'electrolytes':
        return PanelField.ELECTROLYTES_FIELDS.filter(f => f.id !== 'sodiumPotassiumRatio');
      case 'stressAdrenal':
        return [...PanelField.STRESS_ADRENAL_FIELDS];
      case 'lipids':
        return [...PanelField.COMPARISON_LIPIDS_FIELDS];
      case 'inflammation':
        return [...PanelField.COMPARISON_INFLAMMATION_FIELDS];
      case 'liver':
        return [...PanelField.LIVER_FIELDS];
      case 'thyroid':
        return [...PanelField.THYROID_FIELDS];
      case 'hormones':
        return [...PanelField.HORMONE_FIELDS_1, ...PanelField.HORMONE_FIELDS_2].filter(
          f => !['cortisolAM', 'cortisolPM', 'dheaS', 'pregnenolone'].includes(f.id),
        );
      case 'prostate':
        return [...PanelField.PROSTATE_FIELDS];
      default:
        return [];
    }
  }

  /**
   * Renders the PDF sections in the test-results analysis order. Used only when
   * "Match Analysis Order" is checked; the legacy order keeps its own tuned
   * page-break / packing layout untouched.
   *
   * Page-break strategy (works WITH html2pdf 0.9.1's actual engine, not against
   * it — see node_modules/html2pdf.js/src/plugin/pagebreaks.js):
   *
   *  1. Every section's title + table is wrapped in a `.pdf-section` /
   *     `.section-block` pair carrying `page-break-inside: avoid`. For any
   *     section that fits within a single page, html2pdf moves the WHOLE block
   *     to the next page when it would otherwise straddle a boundary — so the
   *     title can never be stranded at the bottom of a page with its table on
   *     the next (the "Kidney Function" orphan bug). `break-after/before: avoid`
   *     would be cleaner but html2pdf silently ignores them; `inside: avoid` is
   *     the only keep-together primitive it honors.
   *
   *  2. html2pdf only applies that whole-block push when the block is <= 1 page
   *     tall; a taller section is left to break naturally, which could again
   *     orphan its title if it happens to start near a page bottom. To preserve
   *     the no-orphan guarantee for EVERY combination of sections, any section
   *     whose visible-row count could approach/exceed one page is forced onto a
   *     fresh page (title lands at the top; only the table body — never the
   *     title — spills across the page boundary).
   */
  private renderPdfSectionsAnalysisOrder(
    history: BloodworkAnalysis[],
    gender: any,
    whiteBloodCellsHasValues: boolean,
  ): React.ReactNode {
    // Sections that always begin on a fresh page (heavy and/or grouped this way
    // historically). Glucose has long been a hard page-start in this report.
    const alwaysBreakBefore = new Set(['glucose', 'hormones', 'prostate']);

    // A section with this many visible marker rows or more is treated as "tall"
    // (could fill or exceed a landscape page) and is started on its own page so
    // its title is guaranteed to land at the top, never orphaned. Kept safely
    // below the ~14–16 rows a landscape page holds, and consistent with the
    // legacy layout's "Nutrients > 10 → own page" tuning.
    const TALL_SECTION_ROW_THRESHOLD = 12;

    const sections = this.buildComparisonSections(
      history,
      gender,
      whiteBloodCellsHasValues,
    );
    // Precompute each section's height class and whether it opens a fresh page,
    // so the "lead the page" rule below can run before we render.
    const meta = sections.map((section, idx) => {
      const visibleRows = this.countVisibleFields(
        this.fieldsForComparisonKey(section.key, history),
        history,
      );
      const isTall = visibleRows >= TALL_SECTION_ROW_THRESHOLD;
      const forcesFreshPage =
        idx > 0 && (alwaysBreakBefore.has(section.key) || isTall);
      return { section, isTall, forcesFreshPage };
    });

    // Whether each section emits a page break before it. Seeded from
    // forcesFreshPage, then adjusted by the lead-the-page rule below.
    const startsFreshPage = meta.map(m => m.forcesFreshPage);

    // Lead-the-page rule: a SHORT section sandwiched between a tall section
    // (which fills/spills its own page) and a following tall section (which
    // would force its own fresh page) gets stranded alone — html2pdf bumps the
    // short block off the spill page via page-break-inside:avoid, and the next
    // tall section then opens a brand-new page below it. This is the reported
    // "Protein Status alone on a page, Immune Activity on the next page" bug.
    // Move the break to BEFORE the short section so it LEADS the fresh page the
    // tall section was going to open, and let the tall section flow directly
    // beneath it (Protein + Immune share a page; everything after moves up).
    // Only applies when the next section forces a page purely by height — never
    // the intentional hard-break sections (Glucose/Hormones/Prostate).
    for (let i = 1; i < meta.length - 1; i++) {
      const prev = meta[i - 1];
      const cur = meta[i];
      const next = meta[i + 1];
      const nextForcedByHeight =
        next.forcesFreshPage && !alwaysBreakBefore.has(next.section.key);
      if (!cur.forcesFreshPage && prev.isTall && nextForcedByHeight) {
        startsFreshPage[i] = true; // short section leads the fresh page
        startsFreshPage[i + 1] = false; // tall section flows beneath it
      }
    }

    return (
      <>
        {meta.map(({ section, isTall }, idx) => {
          const needsFreshPage = startsFreshPage[idx];
          // Hormone Balance has ~29 possible markers and can run several rows
          // past a single page. Render it as page-sized chunks, each with its
          // own column header and a repeated "Hormone Balance (cont.)" heading,
          // instead of letting one oversized table flow (and get sliced
          // mid-row) across the page boundary.
          if (section.key === 'hormones') {
            return (
              <React.Fragment key={section.key}>
                {this.renderHormoneSectionPdf(history, gender, needsFreshPage)}
              </React.Fragment>
            );
          }
          // A section whose title + table is taller than one page cannot be
          // kept together. With the default page-break-inside:avoid on
          // .section-block, html2pdf bumps the whole table to the next page when
          // it would straddle the boundary — which strands the title alone on
          // the forced-fresh page above it. For these tall sections, let the
          // table flow so the title stays glued to its first rows; individual
          // rows stay intact via the `tr` keep-together rule in the style block.
          const flowStyle: React.CSSProperties | undefined = isTall
            ? { pageBreakInside: 'auto', breakInside: 'auto' }
            : undefined;
          return (
            <React.Fragment key={section.key}>
              {needsFreshPage && <div className="html2pdf__page-break" />}
              <div className="pdf-section" style={flowStyle}>
                <div style={{ height: '8px' }} />
                <h5>
                  <strong>{section.title}</strong>
                </h5>
                <div className="section-block" style={flowStyle}>
                  {section.table}
                </div>
                <div style={{ height: '8px' }} />
              </div>
            </React.Fragment>
          );
        })}
      </>
    );
  }

  /** Count how many fields would render given the current hide/show blanks setting.
   *  - When showHiddenRows === false (Hide blank rows is CHECKED):
   *      Only fields with at least one value in the history are counted.
   *  - When showHiddenRows === true (Hide blank rows is UNCHECKED):
   *      All non-hidden fields are counted, even if they have no values.
   */
  private countVisibleFields(
    fields: PanelField[],
    history: BloodworkAnalysis[],
  ): number {
    const hideBlanks = !this.state.showHiddenRows;
    return fields.filter(
      f =>
        !f.isHiddenFromComparison &&
        (hideBlanks ? this.fieldHasAnyValue(f, history) : true),
    ).length;
  }

  /** Returns true if any of the iron markers in PanelField.IRON_FIELDS
   *  has a value anywhere in the history.
   *
   *  Used for the "hide blank rows" rule:
   *  - When hide blank rows is ON:
   *      If none of these iron markers has a value, the entire
   *      "Iron Related Markers" section is hidden (UI + PDF).
   *  - When hide blank rows is OFF:
   *      The section is always shown, even if all values are blank.
   */
  private anyIronCoreMarkerHasValue(history: BloodworkAnalysis[]): boolean {
    const ironOnlyFields = [...PanelField.IRON_FIELDS];
    return this.sectionHasAnyValues(ironOnlyFields, history);
  }

  // ---------- Logo helpers (preview reliability) ----------

  getLogoUrl = (): string => {
    const { currentUserProfile } = this.props;

    return (
      currentUserProfile?.hasCustomLogo()
        ? currentUserProfile.customLogoUrl
        : (logo as any)
    ) as any;
  };

  /** Normalize whatever we store (URL, key, filename) into an S3 key if possible. */
  private extractUserLogoKey = (candidate: string): string | undefined => {
    if (!candidate) return undefined;

    // Already a key like "user-logos/abc.png"
    if (/^user-logos\//i.test(candidate)) {
      return candidate.replace(/^\/+/, '');
    }

    // "public/user-logos/..." (Amplify storage path)
    const publicIdx = candidate.indexOf('public/user-logos/');
    if (publicIdx >= 0) {
      return candidate.slice(publicIdx + 'public/'.length);
    }

    // Any URL that contains "/user-logos/..."
    const seg = '/user-logos/';
    const idx = candidate.indexOf(seg);
    if (idx >= 0) {
      return candidate.slice(idx + 1); // drop the leading '/'
    }

    // Bare filename? assume user-logos/<filename>
    const isUrl = /^https?:\/\//i.test(candidate) || /^data:/i.test(candidate);
    if (!isUrl) {
      const fn = candidate.split('/').pop();
      if (fn) return `user-logos/${fn}`;
    }

    return undefined;
  };

  /** Convert any URL to a data URL for reliable rendering */
  private toDataUrl = async (url: string): Promise<string> => {
    const res = await fetch(url, { credentials: 'omit' });
    const blob = await res.blob();
    return await new Promise<string>(resolve => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result as string);
      reader.readAsDataURL(blob);
    });
  };

  /** Ensure the preview <Image id="logo"> gets a real, loadable data URL. */
  private ensureHeaderPreviewLogo = async () => {
    const { currentUserProfile } = this.props;

    // Fallback bundled asset (always resolvable)
    const bundled =
      (require('../../assets/imgs/logo.png') as any)?.default ||
      (require('../../assets/imgs/logo.png') as any);

    // No custom logo: use bundled default directly, skip all S3 logic
    if (!currentUserProfile?.hasCustomLogo()) {
      try {
        const dataUrl = await this.toDataUrl(bundled);
        this.setState({ headerLogoSrc: dataUrl });
      } catch {
        this.setState({ headerLogoSrc: bundled });
      }
      return;
    }

    const raw = this.getLogoUrl() as any;
    const candidate = (raw?.default ?? raw) as string | undefined;

    // If full URL, convert to data URL
    if (candidate && /^https?:\/\//i.test(candidate)) {
      try {
        const dataUrl = await this.toDataUrl(candidate);
        this.setState({ headerLogoSrc: dataUrl });
        return;
      } catch {
        // fall through to S3/bundled
      }
    }

    // If already a data URL, use it directly
    if (candidate && /^data:/i.test(candidate)) {
      this.setState({ headerLogoSrc: candidate });
      return;
    }

    // Try to sign via Storage.get and convert to data URL
    try {
      const key = this.extractUserLogoKey(candidate);
      if (key) {
        const signed = (await Storage.get(key)) as string;
        const dataUrl = await this.toDataUrl(signed);
        this.setState({ headerLogoSrc: dataUrl });
        return;
      }
    } catch {
      // fall through
    }

    // Fallback to bundled (convert to data URL)
    try {
      const dataUrl = await this.toDataUrl(bundled);
      this.setState({ headerLogoSrc: dataUrl });
    } catch {
      this.setState({ headerLogoSrc: bundled });
    }
  };

  // -------------------------------------------------------

  componentDidMount() {
    // Make sure modal preview shows a loadable logo URL
    this.ensureHeaderPreviewLogo();

    // Seed the section-order toggle from the saved user setting (default on).
    const saved = this.props.currentUserProfile?.comparisonMatchAnalysisOrder;
    if (typeof saved === 'boolean' && saved !== this.state.matchAnalysisOrder) {
      this.setState({ matchAnalysisOrder: saved });
    }

    // Seed the Section Notes column from the practitioner's last choice. It
    // lives in localStorage rather than the profile so it needs no schema
    // change; unset means the default (shown).
    const key = this.sectionNotesStorageKey();
    if (key && localStorage.getItem(key) === 'false') {
      this.setState({ showSectionNotes: false });
    }

    this.props.furtherConsiderationsEntryStore
      ?.loadFurtherConsiderations()
      .then(furtherConsiderations => {
        this.setState({ furtherConsiderations });
      });
  }

  componentDidUpdate(prevProps: Props) {
    // Keep the toggle in sync when the saved setting changes (e.g. the profile
    // finishes loading, or it's changed on the Settings page).
    const prev = prevProps.currentUserProfile?.comparisonMatchAnalysisOrder;
    const next = this.props.currentUserProfile?.comparisonMatchAnalysisOrder;
    if (typeof next === 'boolean' && next !== prev) {
      this.setState({ matchAnalysisOrder: next });
    }
  }

  /** Per-user key for the Section Notes column preference. */
  private sectionNotesStorageKey(): string | null {
    const id = this.props.currentUserProfile?.id;
    return id ? `${id}-comparison-show-section-notes` : null;
  }

  /** Show/hide the Section Notes column (applies to the PDF export too). */
  private handleShowSectionNotesChange = (checked: boolean) => {
    this.setState({ showSectionNotes: checked });
    const key = this.sectionNotesStorageKey();
    if (key) localStorage.setItem(key, String(checked));
  };

  /** Toggle section order and persist the choice to the user's profile. */
  private handleMatchAnalysisOrderChange = (checked: boolean) => {
    this.setState(
      { matchAnalysisOrder: checked },
      this.scrollComparisonTitleIntoView,
    );
    const store = this.props.userProfileUIStore;
    if (store) {
      store.updateComparisonMatchAnalysisOrder(checked);
      store.saveCurrentUserProfile().catch(err => {
        console.error('Failed to save section-order preference', err);
      });
    }
  };

  findPanelMarker(
    panelField: PanelField,
    bloodworkAnalysis: BloodworkAnalysis,
  ): PanelMarker {
    return bloodworkAnalysis.findPanelMarker(panelField.id);
  }

  displayValue(panelMarker: PanelMarker) {
    if (panelMarker) {
      return panelMarker.hasValue()
        ? MathUtils.round(panelMarker.value, panelMarker.field.precision)
        : null;
    } else {
      return 'N/A';
    }
  }

  getClassName(pm: PanelMarker): string {
    if (!pm || pm.isBlank() || pm.isOptimal()) {
      return '';
    }

    if (pm.field.noRedChart) {
      if (pm.isNotOptimal() && pm.isLab()) {
        return 'bg-yellow';
      } else if (!pm.isLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (pm.isGreaterThanAlarm()) {
        return 'bg-red';
      }
    } else if (pm.field.noRedOrangeChart) {
      if (pm.isNotOptimal() && pm.isLessThanOrIsLab()) {
        return 'bg-yellow';
      } else if (pm.isGreaterThanLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (pm.isGreaterThanAlarm()) {
        return 'bg-red';
      }
    } else if (pm.field.useRDWChart) {
      if (pm.isGreaterThanOptimal() && pm.isLab()) {
        return 'bg-yellow';
      } else if (pm.isGreaterThanLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (pm.isGreaterThanAlarm()) {
        return 'bg-red';
      }
    } else {
      if (pm.isNotOptimal() && pm.isLab()) {
        return 'bg-yellow';
      } else if (pm.isNotLab() && pm.isAlarm()) {
        return 'bg-orange';
      } else if (!pm.isAlarm()) {
        return 'bg-red';
      }
    }
    return '';
  }

  getIcon(pm: PanelMarker) {
    if (pm.isBlank()) return null;
    let img: any;
    if (pm.isGreaterThanOptimal()) {
      img = arrow_up;
    } else if (pm.isStricklyLessThanOptimal()) {
      img = arrow_down;
    }
    return <img src={img} alt="" />;
  }

  comparisonDetails(
    bloodworkAnalysisHistory: BloodworkAnalysis[],
    pm: PanelMarker,
    index: number,
  ) {
    if (!pm) {
      return null;
    }

    if (pm.field.id === 'menstrualPhase') {
      return (
        <td>
          {pm.altValue === MenstrualPhase.NONE ? (
            <>
              {MenstrualPhase.NONE}
              <br />
              (children&apos;s ranges)
            </>
          ) : (
            pm.altValue
          )}
        </td>
      );
    }

    let img, imgSrc;
    const valueDetail = (
      <td className={this.getClassName(pm)} key={index}>
        {this.displayValue(pm)}&nbsp;&nbsp;{this.getIcon(pm)}
      </td>
    );
    if (index + 1 < bloodworkAnalysisHistory.length) {
      return valueDetail;
    } else {
      if (
        pm &&
        pm.hasValue() &&
        pm.previousPanelMarker &&
        pm.previousPanelMarker.hasValue()
      ) {
        if (pm.isOptimal() && pm.previousPanelMarker.isOptimal()) {
          // No op
        } else {
          if (
            Math.abs(pm.percentFromMean) <
            Math.abs(pm.previousPanelMarker.percentFromMean)
          ) {
            imgSrc = thumbs_up;
          } else if (
            Math.abs(pm.percentFromMean) >
            Math.abs(pm.previousPanelMarker.percentFromMean)
          ) {
            imgSrc = thumbs_down;
          }
          img = <img src={imgSrc} alt="" />;
        }
      }

      return (
        <>
          {valueDetail}
          <td style={{ textAlign: 'center' }}>{img}</td>
          <td>{pm.range?.alarmMin}</td>
          <td>{pm.range?.labMin}</td>
          <td>{pm.range?.optimalMin}</td>
          <td>{pm.range?.optimalMax}</td>
          <td>{pm.range?.labMax}</td>
          <td>{pm.range?.alarmMax}</td>
        </>
      );
    }
  }

  exportTooltip() {
    return (
      <Tooltip id="export-tooltip">
        <div>Click this icon to export to PDF</div>
      </Tooltip>
    );
  }

  exportJsonTooltip() {
    return (
      <Tooltip id="export-json-tooltip">
        <div>Download all of this client&apos;s data — every test, plus notes and analyses — as a file to use with an AI</div>
      </Tooltip>
    );
  }

  /** Click popover with a prompt users can copy to use with the exported file. */
  exportPromptPopover() {
    return (
      <Popover id="export-prompt-popover" className="export-prompt-popover">
        <Popover.Title
          as="div"
          style={{ fontWeight: 600, backgroundColor: '#fff', borderBottom: 'none' }}
        >
          Using this file with an AI
        </Popover.Title>
        <Popover.Content>
          <div style={{ textAlign: 'left' }}>
            <div style={{ marginBottom: '6px' }}>
              Download the file, then upload it to an AI assistant (Claude, ChatGPT,
              Gemini, etc.) with this prompt:
            </div>
            <div style={{ fontStyle: 'italic' }}>&ldquo;{AI_FILE_PROMPT}&rdquo;</div>
            <CopyPromptButton text={AI_FILE_PROMPT} />
            <div style={{ marginTop: '8px', opacity: 0.85 }}>
              (The file also includes full instructions at the top.)
            </div>
          </div>
        </Popover.Content>
      </Popover>
    );
  }

  exportInfoTooltip() {
    return (
      <Tooltip id="export-info-tooltip">
        <div>
          Click this icon to learn more about the export to PDF feature.
        </div>
      </Tooltip>
    );
  }

  openExportInfoWindow = () => {
    window.open('https://labsmarts.com/exporttopdf');
  };

  exportWithNotesTooltip() {
    return (
      <Tooltip id="export-with-notes-tooltip">
        <div>Click this icon to export to PDF with notes</div>
      </Tooltip>
    );
  }

  matchAnalysisOrderTooltip() {
    return (
      <Tooltip id="match-analysis-order-tooltip">
        <div>
          When checked, sections in the comparison table and PDF follow the same
          order as the Test Results Analysis page. Uncheck to use the legacy
          lab-report order (CBC markers, then CMP, then lipids, etc.). Your
          choice is saved to your settings.
        </div>
      </Tooltip>
    );
  }

  hideBlankRowsTooltip() {
    return (
      <Tooltip id="hide-blank-rows-tooltip">
        <div>
          When checked, marker rows with no recorded value across every
          collection are hidden, and sections with no values drop out entirely.
          Uncheck to show all markers, including blanks.
        </div>
      </Tooltip>
    );
  }

  showSectionNotesTooltip() {
    return (
      <Tooltip id="show-section-notes-tooltip">
        <div>
          Shows each test's Section Notes — the notes typed on the individual
          section tabs — in their own column beside the Insights note, which
          summarises the whole test. Applies to the PDF export as well. Section
          notes are edited on that test's own report, not here.
        </div>
      </Tooltip>
    );
  }

  /** A thin vertical rule used to separate groups in the card header. */
  headerDivider() {
    return (
      <span
        aria-hidden="true"
        style={{
          width: '1px',
          height: '20px',
          backgroundColor: 'rgba(255, 255, 255, 0.5)',
          alignSelf: 'center',
        }}
      />
    );
  }

  export(withNotes = false) {
    this.props.onExportToPdf();
    if (this.props.currentUserProfile.pdfExportComparisonTableAllowed) {
      this.setState({
        showPdfModal: true,
        numResultsToCompare: NUM_RESULTS_TO_COMPARE_FOR_PDF,
        withNotes,
      });
    } else {
      this.setState({
        showUpgradeModal: true,
      });
    }
  }

  exportJson = () => {
    const { bloodworks, bloodworkRanges, clientEntryStore } = this.props;
    const { furtherConsiderations } = this.state;

    // "Overall Practitioner Notes" is a client-level field — read it from the LIVE
    // client (store), NOT the per-test snapshot, so a practitioner edit/delete is
    // reflected. All tests share one clientId, so bloodworks[0] is sufficient.
    const clientId = bloodworks && bloodworks[0] ? bloodworks[0].clientId : undefined;
    const liveClient =
      clientEntryStore?.clients?.find(c => c.id === clientId) ||
      (bloodworks && bloodworks[0] ? bloodworks[0].client : undefined);

    const exportData = buildAIExportData(
      bloodworks,
      bloodworkRanges,
      furtherConsiderations,
      liveClient?.overallPractitionerNotes,
    );

    const jsonString = JSON.stringify(exportData, null, 2);
    // Declare UTF-8 so consumers don't fall back to Latin-1/CP1252 and mojibake
    // the µ in units (µmol/L, etc.). Direction glyphs are already spelled out.
    const blob = new Blob([jsonString], {
      type: 'application/json;charset=utf-8',
    });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');

    // Name the file with the client's initials + the latest collection date, so
    // the practitioner can recognize the download. This is only for their local
    // use — the file is fed to Bedrock (under the AWS BAA) and the AI receives
    // the JSON contents, not the filename.
    const client = bloodworks && bloodworks[0] ? bloodworks[0].client : undefined;
    const initials = client
      ? `${(client.firstName || '').charAt(0)}${(client.lastName || '').charAt(0)}`
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, '')
      : '';
    const collectionDates = (bloodworks || [])
      .map(bw => bw && bw.collectionDate)
      .filter(Boolean) as string[];
    const latestCollectionDate = collectionDates.length
      ? collectionDates.reduce((a, b) => (a > b ? a : b))
      : undefined;
    const datePart = moment(latestCollectionDate).format('YYYY-MM-DD');
    const filename = initials
      ? `blood_work_raw_data_${initials}_${datePart}.json`
      : `blood_work_raw_data_${datePart}.json`;

    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  comparisonTable(
    bloodworkAnalysisHistory: BloodworkAnalysis[],
    panelFields: PanelField[],
    clientGender?: string, //For menstrual phase - not showing if gender is male
  ) {
    // Determine which rows to show
    const hideBlanks = !this.state.showHiddenRows; // checkbox checked = hide blanks

    const visibleFields = panelFields.filter((field: PanelField) => {
      if (field.isHiddenFromComparison) return false;

      // Never show menstrual phase for male clients
      if (field.id === 'menstrualPhase' && clientGender === 'M') {
        return false;
      }

      const hasAny = this.fieldHasAnyValue(field, bloodworkAnalysisHistory);
      // When hideBlanks is true: only fields with data (incl. menstrualPhase via altValue).
      // When hideBlanks is false: show all fields (incl. menstrualPhase) if not male.
      return hideBlanks ? hasAny : true; // default hide; show all when unchecked
    });

    // If nothing to show, don't render a table at all
    if (visibleFields.length === 0) {
      return null;
    }

    return (
      <Table striped bordered hover className="labsmartz-table">
        <thead>
          <tr>
            <th>Marker</th>
            <th>Units</th>
            {bloodworkAnalysisHistory.map(
              (bloodworkAnalysis: BloodworkAnalysis, index: number) => {
                return (
                  <th key={index}>
                    <Moment format="MM/DD/YYYY">
                      {bloodworkAnalysis.bloodwork.collectionDate}
                    </Moment>
                  </th>
                );
              },
            )}
            <th>Trend</th>
            <th>Alarm Min</th>
            <th>Lab Min</th>
            <th>Optimal Min</th>
            <th>Optimal Max</th>
            <th>Lab Max</th>
            <th>Alarm Max</th>
          </tr>
        </thead>

        <tbody>
          {visibleFields.map((field: PanelField, rowIdx: number) => {
            const valueCells = bloodworkAnalysisHistory.map((bwa, colIdx) => {
              const pm: PanelMarker | undefined = this.findPanelMarker(
                field,
                bwa,
              );

              if (pm && field.id === 'menstrualPhase') {
                return (
                  <td key={`v-${rowIdx}-${colIdx}`}>
                    {pm.altValue === MenstrualPhase.NONE ? (
                      <>
                        {MenstrualPhase.NONE}
                        <br />
                        (children&apos;s ranges)
                      </>
                    ) : (
                      pm.altValue
                    )}
                  </td>
                );
              }

              return (
                <td
                  key={`v-${rowIdx}-${colIdx}`}
                  className={pm ? this.getClassName(pm) : ''}
                >
                  {pm?.hasValue?.() ? this.displayValue(pm) : null}
                  &nbsp;&nbsp;
                  {pm ? this.getIcon(pm) : null}
                </td>
              );
            });

            const lastPm = this.findPanelMarker(
              field,
              bloodworkAnalysisHistory[bloodworkAnalysisHistory.length - 1],
            );

            let trend: React.ReactNode = null;
            if (
              lastPm?.hasValue?.() &&
              lastPm.previousPanelMarker?.hasValue?.() &&
              !(lastPm.isOptimal() && lastPm.previousPanelMarker.isOptimal())
            ) {
              const improved =
                Math.abs(lastPm.percentFromMean) <
                Math.abs(lastPm.previousPanelMarker.percentFromMean);
              const worsened =
                Math.abs(lastPm.percentFromMean) >
                Math.abs(lastPm.previousPanelMarker.percentFromMean);
              trend = improved ? (
                <img src={thumbs_up} alt="" />
              ) : worsened ? (
                <img src={thumbs_down} alt="" />
              ) : null;
            }

            return (
              <tr key={`r-${rowIdx}`}>
                <td>{field.getChartLabel()}</td>
                <td>{field.getChartUnits()}</td>
                {valueCells}
                <td style={{ textAlign: 'center' }}>{trend}</td>
                <td>{lastPm?.range?.alarmMin ?? ''}</td>
                <td>{lastPm?.range?.labMin ?? ''}</td>
                <td>{lastPm?.range?.optimalMin ?? ''}</td>
                <td>{lastPm?.range?.optimalMax ?? ''}</td>
                <td>{lastPm?.range?.labMax ?? ''}</td>
                <td>{lastPm?.range?.alarmMax ?? ''}</td>
              </tr>
            );
          })}
        </tbody>
      </Table>
    );
  }

  practitionerNotesTable(
    bloodworkAnalysisHistory: BloodworkAnalysis[],
    isPdf: boolean = false,
  ) {
    // One row per collection date, carrying that test's Insights note AND its
    // Section Notes — a test whose notes were all typed in the section tabs has
    // no Insights note at all, and must still appear here.
    const notesArray = bloodworkAnalysisHistory
      .map(bwa => ({
        bloodwork: bwa.bloodwork,
        insights: insightsTextFor(bwa?.bloodwork),
        sectionNotes: sectionNotesTextFor(bwa),
      }))
      .filter(entry => entry.insights || entry.sectionNotes);

    if (notesArray.length === 0) {
      return null;
    }

    // Sort newer to older
    let sortedNotes = [...notesArray];
    sortedNotes.sort(
      (a, b) =>
        new Date(b.bloodwork.collectionDate).getTime() -
        new Date(a.bloodwork.collectionDate).getTime(),
    );

    // The Section Notes column only earns its width when the practitioner has
    // it switched on AND some test actually has section notes.
    const showSectionNotes =
      this.state.showSectionNotes && sortedNotes.some(e => !!e.sectionNotes);

    if (isPdf) {
      // Pre-paginated in JS: html2pdf can't split a table row, so each chunk is
      // one page's worth of rows and the heading repeats as "(cont.)".
      const noteChunks = paginateNoteRows(
        sortedNotes.map(entry => ({
          collectionDate: entry.bloodwork.collectionDate,
          insights: entry.insights,
          sectionNotes: entry.sectionNotes,
        })),
        { showSectionNotes },
      );

      return noteChunks.map((chunk, chunkIdx) => (
        // pageBreakInside: 'auto' overrides the .pdf-section avoid rule so the
        // wrapper isn't ALSO whole-pushed to a fresh page by html2pdf. Without it,
        // the wrapper push + the inner html2pdf__page-break stack into a double
        // break, leaving a blank page before each chunk. The inner .section-block
        // keeps its own page-break-inside: avoid, so the chunk table stays whole.
        // (Matches the Overall Progress & Trends chunk below.)
        <div
          key={`note-table-${chunkIdx}`}
          className="pdf-section"
          style={{ pageBreakInside: 'auto' }}
        >
          <div className="html2pdf__page-break" />
          <div style={{ height: TOP_OF_PAGE_SPACER }} />
          {/* Blue header + outline, matching the Disclaimer and Overall
              Progress & Trends cards. NOT "disclaimer-panel": that class is in
              html2pdf's break-inside:avoid list, which would stop these chunks
              from paginating. */}
          <div className="section-block">
            <Card style={{ marginTop: 0 }}>
              <Card.Header className="py-1">
                {showSectionNotes
                  ? 'Insights & Section Notes by Collection Date'
                  : 'Insights by Collection Date'}
                {chunkIdx > 0 ? ' (cont.)' : ''}
              </Card.Header>
              <Card.Body className="p-0">
                <Table
                  bordered
                  className="labsmartz-table labsmartz-notes-table"
                  style={{ marginTop: 0, marginBottom: 0, width: '100%' }}
                >
                  <thead>
                    <tr>
                      <th style={{ width: showSectionNotes ? '15%' : '20%' }}>
                        Collection Date
                      </th>
                      <th style={showSectionNotes ? { width: '45%' } : undefined}>
                        Insights
                      </th>
                      {showSectionNotes && <th>Section Notes</th>}
                    </tr>
                  </thead>
                  <tbody>
                    {chunk.map((item, idx) => {
                      const noteCell = (content: string, key: string) => (
                        <td>
                          <div
                            style={{
                              wordBreak: 'break-word',
                              color: NOTE_TEXT_COLOR,
                            }}
                          >
                            {content.split('\n').map((line: string, lIdx: number) => (
                              <div
                                key={`${key}-${lIdx}`}
                                style={{
                                  pageBreakInside: 'avoid',
                                  whiteSpace: 'pre-wrap',
                                }}
                              >
                                {renderNoteLine(line)}
                              </div>
                            ))}
                          </div>
                        </td>
                      );
                      return (
                        // No zebra striping: these rows are text panels, not data
                        // rows, and a shaded one reads as a grey box beside the
                        // white Overall Progress & Trends card.
                        <tr key={`note-${chunkIdx}-${idx}`}>
                          <td>
                            <Moment format="MM/DD/YYYY">
                              {item.collectionDate}
                            </Moment>
                            {item.isCont && (
                              <span
                                style={{ fontSize: '0.8em', marginLeft: '4px' }}
                              >
                                (cont.)
                              </span>
                            )}
                          </td>
                          {noteCell(item.insightsSplit || '', 'ins')}
                          {showSectionNotes &&
                            noteCell(item.sectionSplit || '', 'sec')}
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </Card.Body>
            </Card>
          </div>
          <div style={{ height: '8px' }} />
        </div>
      ));
    }

    // For web UI, no chunking needed. Just show the sorted array.
    return (
      // Not striped — see labsmartz-notes-table in index.css.
      <Table bordered hover className="labsmartz-table labsmartz-notes-table">
        <thead>
          <tr>
            <th style={{ width: showSectionNotes ? '15%' : '20%' }}>
              Collection Date
            </th>
            <th style={showSectionNotes ? { width: '40%' } : undefined}>
              Insights
            </th>
            {showSectionNotes && (
              <th style={{ width: '37%' }}>Section Notes</th>
            )}
            <th style={{ width: showSectionNotes ? '8%' : '10%' }}>Action</th>
          </tr>
        </thead>
        <tbody>
          {sortedNotes.map((entry, idx) => {
            return (
              <PractitionerNoteRow
                // Keyed by test, not position: the row list is filtered to the
                // tests that have notes, so a test gaining its first note
                // shifts every index below it — and an index key would hand one
                // row's typed-but-unsaved note to a different test.
                key={entry.bloodwork.id || `idx-${idx}`}
                bloodwork={entry.bloodwork}
                bloodworkEntryStore={this.props.bloodworkEntryStore}
                // Its own column, and not editable here — the section notes
                // belong to that test's own report.
                sectionNotesText={entry.sectionNotes}
                showSectionNotesColumn={showSectionNotes}
              />
            );
          })}
        </tbody>
      </Table>
    );
  }

  onHidePdfModal() {
    this.props.onHidePdfModal();
    this.setState({
      showPdfModal: false,
      numResultsToCompare: NUM_RESULTS_TO_COMPARE,
      withNotes: false,
    });
  }

  render() {
    const { bloodworkRanges, currentUserProfile } = this.props;
    const {
      showUpgradeModal,
      showPdfModal,
      numResultsToCompare,
      headerLogoSrc,
      furtherConsiderations,
    } = this.state;

    let { bloodworks } = this.props;
    if (!bloodworks || bloodworks.length === 0) {
      return null;
    }

    if (!currentUserProfile.showClientComparisonTable) {
      return null;
    }

    bloodworks = bloodworks.slice(0, numResultsToCompare);

    const bloodworkAnalysisHistory: BloodworkAnalysis[] = bloodworks
      .reverse()
      .map(bloodwork => {
        const bloodworkAnalysis = new BloodworkAnalysis({
          bloodwork,
          bloodworkRanges,
          bloodworkFields: PanelField.ALL_FIELDS,
        });
        bloodworkAnalysis.init();
        bloodworkAnalysis.populatePanelMarkersArray();
        return bloodworkAnalysis;
      });

    if (bloodworkAnalysisHistory.length >= 2) {
      bloodworkAnalysisHistory[
        bloodworkAnalysisHistory.length - 1
      ].preparePreviousPanelMarkers(
        bloodworkAnalysisHistory[bloodworkAnalysisHistory.length - 2],
      );
    }
    const today = new Date().toISOString();
    const mostRecentBloodwork = bloodworks[bloodworks.length - 1];
    const actualClient =
      this.props.clientEntryStore?.clients?.find(
        c => c.id === mostRecentBloodwork.clientId,
      ) || mostRecentBloodwork.client;

    // Fallback logo if preview URL hasn’t resolved yet
    const fallbackLogo =
      (require('../../assets/imgs/logo.png') as any)?.default ||
      (require('../../assets/imgs/logo.png') as any);

    const logoSrc = headerLogoSrc || fallbackLogo;
    console.log(
      '[BloodworkComparison] Render - headerLogoSrc:',
      headerLogoSrc ? `${headerLogoSrc.substring(0, 50)}...` : 'null',
    );

    // Check if the whiteBloodCells marker has any value across the history
    const whiteBloodCellsField = PanelField.ALL_FIELDS.find(
      f => f.id === 'whiteBloodCells',
    );
    const whiteBloodCellsHasValues = whiteBloodCellsField
      ? this.fieldHasAnyValue(whiteBloodCellsField, bloodworkAnalysisHistory)
      : false;

    // ---- Updated packing logic for Nutrients / Electrolytes / Lipids ----
    const hideBlanksPdf = !this.state.showHiddenRows;

    const nutrientsFields = [
      ...PanelField.NUTRIENTS_FIELDS,
      ...PanelField.ZINC_FIELDS,
    ];
    // Na/K ratio moved to the Stress & Adrenal section (mirrors the
    // analysis-order path); exclude it here so it doesn't render in both.
    const electrolytesFields = PanelField.ELECTROLYTES_FIELDS.filter(
      f => f.id !== 'sodiumPotassiumRatio',
    );
    const lipidsFields = [...PanelField.COMPARISON_LIPIDS_FIELDS];
    const inflammationFields = [...PanelField.COMPARISON_INFLAMMATION_FIELDS];
    const nutrientsCount = this.countVisibleFields(
      nutrientsFields,
      bloodworkAnalysisHistory,
    );
    const lipidsCount = this.countVisibleFields(
      lipidsFields,
      bloodworkAnalysisHistory,
    );

    /**
     * Packing rules:
     * 1 Nutrients ≤ 5  and Lipids ≤ 6 → Nutrients, Electrolytes, and Lipids all on the same page.
     * 2 Nutrients 6–10               → Nutrients + Electrolytes on same page; Lipids start new page.
     * 3 Nutrients > 10               → Electrolytes + Lipids together on next page.
     */

    const packElectrolytesWithNutrients =
      hideBlanksPdf && nutrientsCount > 0 && nutrientsCount <= 10;

    const packLipidsWithNutElec =
      hideBlanksPdf &&
      nutrientsCount > 0 &&
      nutrientsCount <= 5 &&
      lipidsCount > 0 &&
      lipidsCount <= 6;

    const packLipidsWithElectrolytes =
      hideBlanksPdf && nutrientsCount > 10 && lipidsCount > 0;

    // Count how many Hormone rows will actually render in the PDF,
    // honoring "Hide blank rows" and hiding menstrual phase for males.
    const hormoneFieldsBaseForCount = [
      ...PanelField.HORMONE_FIELDS_1,
      ...PanelField.HORMONE_FIELDS_2,
    ].filter(f => f.id !== 'menstrualPhase' && !['cortisolAM', 'cortisolPM', 'dheaS', 'pregnenolone'].includes(f.id));

    const menstrualPhaseFieldForCount = PanelField.ALL_FIELDS.find(
      f => f.id === 'menstrualPhase',
    );

    const hormoneFieldsForCountRaw = [
      ...(menstrualPhaseFieldForCount ? [menstrualPhaseFieldForCount] : []),
      ...hormoneFieldsBaseForCount,
    ];

    // Deduplicate and apply the "no menstrual phase for male" rule
    const hormoneFieldsForCount = hormoneFieldsForCountRaw
      .filter((f, i, a) => a.findIndex(g => g.id === f.id) === i)
      .filter(
        f => !(f.id === 'menstrualPhase' && actualClient?.gender === 'M'),
      );

    const hormoneVisibleCount = this.countVisibleFields(
      hormoneFieldsForCount,
      bloodworkAnalysisHistory,
    );

    return (
      <>
        <PDFModal
          show={showPdfModal}
          title="Blood Work Comparison"
          client={actualClient}
          collectionDate={mostRecentBloodwork.collectionDate}
          elementId="pdf-comparison-results"
          onHide={() => this.onHidePdfModal()}
          preloadedLogoDataUrl={headerLogoSrc}
        >
          <div
            id="pdf-comparison-results"
            style={{ paddingBottom: 10, marginBottom: 10, transform: 'none' }}
          >
            <style>
              {`
                /* Page-break divs: force a break but take up no vertical space */
                #pdf-comparison-results .html2pdf__page-break {
                  height: 0 !important;
                  margin: 0 !important;
                  padding: 0 !important;
                  border: 0 !important;
                  page-break-after: always !important;
                }

                #pdf-comparison-results h3,
                #pdf-comparison-results h5 {
                  margin-top: 8px !important;
                }

                #pdf-comparison-results table.labsmartz-table {
                  margin-top: 0 !important;
                }

                /* Keep each section's title glued to its table. This is the ONLY
                   "keep-together" primitive html2pdf 0.9.1 honors: break-after/
                   before:avoid are ignored by its pagebreak engine, but an element
                   with break-inside:avoid that is <= 1 page tall is pushed whole to
                   the next page when it would straddle a boundary (see
                   node_modules/html2pdf.js/src/plugin/pagebreaks.js, lines 104-113).
                   Sections that can exceed one page are forced onto a fresh page by
                   the renderer, so their title still lands at the top and is never
                   orphaned. */
                #pdf-comparison-results .pdf-section,
                #pdf-comparison-results .pdf-section .section-block {
                  break-inside: avoid;
                  page-break-inside: avoid;
                }

                /* Keep each data row intact. A tall section's table is allowed
                   to flow across a page boundary (see the isTall branch in
                   renderPdfSectionsAnalysisOrder); without this, html2pdf would
                   slice through a row mid-height at the break. */
                #pdf-comparison-results table.labsmartz-table tr {
                  break-inside: avoid;
                  page-break-inside: avoid;
                }

                /* The Disclaimer is pushed whole to a fresh page by the avoid
                   rule, which lands it EXACTLY on the page boundary — and the
                   slice grid rounds down to whole device pixels, so the top of
                   the blue header was being shaved onto the page above. The
                   padding is inside the pushed element, so those lost pixels
                   are blank. Same trick as .pdf-keep-together-block in
                   containers/pdf.css. */
                #pdf-comparison-results .pdf-disclaimer-block {
                  break-inside: avoid;
                  page-break-inside: avoid;
                  padding-top: 24px;
                }
              `}
            </style>
            <br />
            <br />
            <br />
            <div className="text-center">
              <Image
                id="logo"
                src={logoSrc}
                alt="Current Logo"
                fluid
                style={{
                  maxHeight: '100px',
                  maxWidth: '400px',
                  objectFit: 'contain',
                }}
                onError={(e: any) => {
                  const img = e.target as HTMLImageElement;
                  if (img.src !== fallbackLogo) {
                    img.src = fallbackLogo;
                  }
                }}
              />
            </div>
            <div>
              <br />
              <br />
              <h3 className="text-center">
                <strong>Blood Work Comparison Report</strong>
              </h3>
            </div>
            <br />
            <br />
            <br />
            <br />
            <br />
            <br />
            {/* html2pdf__page-break will add a page break after the html element */}
            <div>
              <h5>
                <strong>Prepared for</strong>
              </h5>
              <h5>
                {actualClient.firstName} {actualClient.lastName}
              </h5>
              <br />
              {currentUserProfile.preparedBy && (
                <>
                  <h5>
                    <strong>Prepared by</strong>
                  </h5>
                  <h5>{currentUserProfile.preparedBy}</h5>
                  <br />
                </>
              )}
              <h5>
                <strong>Most Recent Collection Date</strong>
              </h5>
              <h5>
                <Moment format="MM/DD/YYYY">
                  {mostRecentBloodwork.collectionDate}
                </Moment>
              </h5>
              <br />
              <h5>
                <strong>Analysis Date</strong>
              </h5>
              <h5>
                <Moment format="MM/DD/YYYY">{today}</Moment>
              </h5>
              <br />
            </div>

            <div
              className="html2pdf__page-break"
              style={{ height: 0, margin: 0, padding: 0, border: 0 }}
            />

            {this.state.matchAnalysisOrder ? (
              this.renderPdfSectionsAnalysisOrder(
                bloodworkAnalysisHistory,
                actualClient.gender,
                whiteBloodCellsHasValues,
              )
            ) : (
              <>
                {/* RBC */}
                {(() => {
              const table = this.comparisonTable(bloodworkAnalysisHistory, [
                ...PanelField.RBC_FIELDS,
              ]);
              return table ? (
                <div className="pdf-section">
                  <div style={{ height: '8px' }} />
                  <h5>
                    <strong>Blood Oxygen & Red Blood Cell Function</strong>
                  </h5>
                  <div className="section-block">{table}</div>
                  <div style={{ height: '8px' }} />
                </div>
              ) : null;
            })()}

            {/* Iron + Copper */}
            {(() => {
              const hideBlanks = !this.state.showHiddenRows;

              if (
                hideBlanks &&
                !this.anyIronCoreMarkerHasValue(bloodworkAnalysisHistory)
              ) {
                return null;
              }

              const table = this.comparisonTable(bloodworkAnalysisHistory, [
                ...PanelField.IRON_FIELDS,
                ...PanelField.COPPER_FIELDS,
              ]);
              return table ? (
                <div className="pdf-section">
                  <div style={{ height: '8px' }} />
                  <h5>
                    <strong>Iron Status & Utilization</strong>
                  </h5>
                  <div className="section-block">{table}</div>
                  <div style={{ height: '8px' }} />
                </div>
              ) : null;
            })()}

            {/* Immune + WBC Diff */}
            {(() => {
              // When "Hide blank rows" is checked, hide the entire immune section if whiteBloodCells has no values
              if (!whiteBloodCellsHasValues && hideBlanksPdf) return null;

              const table = this.comparisonTable(bloodworkAnalysisHistory, [
                ...PanelField.IMMUNE_SYSTEM_FIELDS,
                ...PanelField.WBC_ABSOLUTE_DIFFERENTIALS,
              ]);
              return table ? (
                <>
                  {hideBlanksPdf && (
                <div
                  className="html2pdf__page-break"
                  style={{ height: 0, margin: 0, padding: 0, border: 0 }}
                />
                )}
                <div className="pdf-section">
                  <div style={{ height: '8px' }} />
                  <h5>
                    <strong>Immune Activity</strong>
                  </h5>
                  <div className="section-block">{table}</div>
                  <div style={{ height: '8px' }} />
                </div>
                </>
              ) : null;
            })()}

            {/* Coagulation */}
            {(() => {
              const table = this.comparisonTable(
                bloodworkAnalysisHistory,
                this.plateletsComparisonFields(bloodworkAnalysisHistory),
              );
              return table ? (
                <div className="pdf-section">
                  <div style={{ height: '8px' }} />
                  <h5>
                    <strong>Blood Clotting Function</strong>
                  </h5>
                  <div className="section-block">{table}</div>
                  <div style={{ height: '8px' }} />
                </div>
              ) : null;
            })()}

            {/* Glucose */}
            {(() => {
              const glucoseTable = this.comparisonTable(
                bloodworkAnalysisHistory,
                [...PanelField.GLUCOSE_FIELDS, ...PanelField.GLUCOSE_TG_HDL_SECTION_FIELDS],
              );

              return glucoseTable ? (
                <>
                  {/* Always start Glucose on a new page */}
                  <div className="html2pdf__page-break" />
                  <div className="pdf-section">
                    <div style={{ height: '8px' }} />
                    <h5>
                      <strong>
                        Glucose Regulation & Metabolic Function
                      </strong>
                    </h5>
                    <div className="section-block">{glucoseTable}</div>
                    <div style={{ height: '8px' }} />
                  </div>
                </>
              ) : null;
            })()}

            {/* Kidney */}
            {(() => {
              const table = this.comparisonTable(bloodworkAnalysisHistory, [
                ...PanelField.KIDNEY_FIELDS,
              ]);
              return table ? (
                <div className="pdf-section">
                  <div style={{ height: '8px' }} />
                  <h5>
                    <strong>Kidney Function</strong>
                  </h5>
                  <div className="section-block">{table}</div>
                  <div style={{ height: '8px' }} />
                </div>
              ) : null;
            })()}

            {/* Protein */}
            {(() => {
              const table = this.comparisonTable(bloodworkAnalysisHistory, [
                ...PanelField.PROTEINS_FIELDS,
              ]);
              return table ? (
                <div className="pdf-section">
                  <div style={{ height: '8px' }} />
                  <h5>
                    <strong>Protein Status</strong>
                  </h5>
                  <div className="section-block">{table}</div>
                  <div style={{ height: '8px' }} />
                </div>
              ) : null;
            })()}

            {/* Nutrients + Zinc */}
            {(() => {
              const table = this.comparisonTable(
                bloodworkAnalysisHistory,
                nutrientsFields,
              );
              return table ? (
                <>
                  {/* Always start Nutrients on a new page (unchanged) */}
                  {hideBlanksPdf && (
                  <div className="html2pdf__page-break" />
                  )}
                  <div className="pdf-section">
                    <div style={{ height: '8px' }} />
                    <h5>
                      <strong>Vitamin & Mineral Status</strong>
                    </h5>
                    <div className="section-block">{table}</div>
                    <div style={{ height: '8px' }} />
                  </div>
                </>
              ) : null;
            })()}

            {/* Electrolytes */}
            {(() => {
              const table = this.comparisonTable(
                bloodworkAnalysisHistory,
                electrolytesFields,
              );
              if (!table) return null;

              // Only insert the page break if we are NOT packing Electrolytes with Nutrients
              const needBreakBeforeElectrolytes =
                !packElectrolytesWithNutrients;

              return (
                <>
                  {needBreakBeforeElectrolytes && (
                    <div className="html2pdf__page-break" />
                  )}
                  <div className="pdf-section">
                    <div style={{ height: '8px' }} />
                    <h5>
                      <strong>Electrolyte Balance</strong>
                    </h5>
                    <div className="section-block">{table}</div>
                    <div style={{ height: '8px' }} />
                  </div>
                </>
              );
            })()}

            {/* ---- PDF section CSS (once for this group) ---- */}
            <style>
              {`
              /* Keep entire section blocks intact across page boundaries */
              .pdf-section,
              .pdf-section .section-block {
                break-inside: avoid;
                page-break-inside: avoid;
              }
              .pdf-section h5 {
                break-after: avoid;
                page-break-after: avoid;
                margin-bottom: 6px;
              }
            `}
            </style>

            {/* ---- Lipids / Liver / Thyroid packing ---- */}
            {(() => {
              const lipidsTable = this.comparisonTable(
                bloodworkAnalysisHistory,
                lipidsFields,
              );
              const liverTable = this.comparisonTable(
                bloodworkAnalysisHistory,
                [...PanelField.LIVER_FIELDS],
              );
              const thyroidTable = this.comparisonTable(
                bloodworkAnalysisHistory,
                [...PanelField.THYROID_FIELDS],
              );
              const inflammationTable = this.comparisonTable(
                bloodworkAnalysisHistory,
                inflammationFields,
              );

              const showingBlanks = this.state.showHiddenRows; // true = showing blanks in UI/PDF
              const hideBlanks = !showingBlanks; // true = rows hidden (apply new rules)

              const Section = ({
                title,
                table,
                className = 'pdf-section',
              }: {
                title: string;
                table: React.ReactNode | null;
                className?: string;
              }) =>
                table ? (
                  <div className={className}>
                    <div style={{ height: '16px' }} />
                    <h5>
                      <strong>{title}</strong>
                    </h5>
                    <div className="section-block">{table}</div>
                    <div style={{ height: '8px' }} />
                  </div>
                ) : null;

              if (lipidsTable) {
                // When showing blanks, keep Electrolytes + Lipids on the same page.
                // When hiding blanks, insert a break before Lipids unless we're packing with Nutrients/Electrolytes.
                const needBreakBeforeLipids = showingBlanks
                  ? false
                  : !(packLipidsWithNutElec || packLipidsWithElectrolytes);

                return (
                  <>
                    {needBreakBeforeLipids && (
                      <div className="html2pdf__page-break" />
                    )}

                    <Section title="Lipid Balance" table={lipidsTable} />

                    <Section
                      title="Inflammation & Oxidative Stress"
                      table={inflammationTable}
                    />

                    {hideBlanks && needBreakBeforeLipids ? (
                      // Rows hidden AND Lipids were moved to a new page:
                      // Put LIVER on the SAME page as Lipids, then THYROID on the NEXT page.
                      <>
                        <Section
                          title="Liver Function"
                          table={liverTable}
                        />
                        {thyroidTable ? (
                          <>
                            <div className="html2pdf__page-break" />
                            <Section
                              title="Thyroid Function"
                              table={thyroidTable}
                            />
                          </>
                        ) : null}
                      </>
                    ) : (
                      // Liver and Thyroid each flow as their OWN keep-together
                      // block. They were previously wrapped in a single
                      // page-break-inside:avoid container (the dead
                      // `starts-on-new-page` class has no CSS). When Liver+Thyroid
                      // were small enough to fit one page together, html2pdf's
                      // keep-together rule pushed BOTH to a fresh page — stranding
                      // Liver even when the sparse Inflammation section just above
                      // still had room. Rendering them as independent sections lets
                      // Liver pack onto the Inflammation page and fill that space
                      // (it also flows the same way when tables are full).
                      <>
                        <Section
                          title="Liver Function"
                          table={liverTable}
                        />
                        <Section title="Thyroid Function" table={thyroidTable} />
                      </>
                    )}
                  </>
                );
              }

              // No Lipids
              if (hideBlanks) {
                // Rows hidden & NO Lipids: Inflammation (if any), then Liver and
                // Thyroid on the SAME page.
                return (
                  <>
                    <Section
                      title="Inflammation & Oxidative Stress"
                      table={inflammationTable}
                    />
                    <Section
                      title="Liver Function"
                      table={liverTable}
                    />
                    <Section title="Thyroid Function" table={thyroidTable} />
                  </>
                );
              }

              // Showing blanks & NO Lipids: keep your previous behavior (Thyroid starts on next page if present)
              return (
                <>
                  <Section
                    title="Inflammation & Oxidative Stress"
                    table={inflammationTable}
                  />
                  <Section title="Liver Function" table={liverTable} />
                  {thyroidTable ? (
                    <>
                      <div className="html2pdf__page-break" />
                      <Section title="Thyroid Function" table={thyroidTable} />
                    </>
                  ) : null}
                </>
              );
            })()}

            {/* Stress & Adrenal Balance — renders above Hormones in both the
                analysis order and this legacy order (mirrors comparisonSectionOrder
                and buildComparisonSections, where stressAdrenal precedes hormones). */}
            {(() => {
              const table = this.comparisonTable(bloodworkAnalysisHistory, [
                ...PanelField.STRESS_ADRENAL_FIELDS,
              ]);
              return table ? (
                <div className="pdf-section">
                  <div style={{ height: '8px' }} />
                  <h5>
                    <strong>Stress & Adrenal Balance</strong>
                  </h5>
                  <div className="section-block">{table}</div>
                  <div style={{ height: '8px' }} />
                </div>
              ) : null;
            })()}

            {/* Hormones */}
            {(() => {
              // Liver/Thyroid render in their own blocks above; their presence
              // only decides whether Hormone Balance starts on a fresh page.
              const liverTable = this.comparisonTable(
                bloodworkAnalysisHistory,
                [...PanelField.LIVER_FIELDS],
              );
              const thyroidTable = this.comparisonTable(
                bloodworkAnalysisHistory,
                [...PanelField.THYROID_FIELDS],
              );

              // Page-chunked render so an oversized Hormone Balance table splits
              // across pages with repeated "(cont.)" headings instead of being
              // sliced mid-row.
              return this.renderHormoneSectionPdf(
                bloodworkAnalysisHistory,
                actualClient.gender,
                Boolean(liverTable || thyroidTable),
              );
            })()}

            {/* Prostate */}
            {(() => {
              const prostateTable = this.comparisonTable(
                bloodworkAnalysisHistory,
                [...PanelField.PROSTATE_FIELDS],
              );

              if (!prostateTable) return null;

              const hideBlanks = !this.state.showHiddenRows;

              // When "Hide blank rows" is checked AND Hormones fits on a single
              // page, keep Prostate on the same page as Hormones. Once Hormones
              // spills past one page it is chunked (the last chunk is its own
              // "(cont.)" page), so Prostate gets its own page instead.
              const placeOnSamePageAsHormones =
                hideBlanks &&
                hormoneVisibleCount > 0 &&
                hormoneVisibleCount <= HORMONE_ROWS_PER_PDF_PAGE;

              return (
                <>
                  {/* Only force a new page when NOT packing with Hormones */}
                  {!placeOnSamePageAsHormones && (
                    <div className="html2pdf__page-break" />
                  )}
                  <div
                    className="hormone-pdf-section"
                    style={{
                      marginBottom: 0,
                      paddingBottom: 10,
                      pageBreakInside: 'avoid',
                      breakInside: 'avoid',
                    }}
                  >
                    <div style={{ height: '8px' }} />
                    <h5>
                      <strong>Prostate Function</strong>
                    </h5>
                    <div>{prostateTable}</div>
                    <div style={{ height: '8px' }} />
                  </div>
                </>
              );
            })()}
              </>
            )}

            {/* Practitioner Notes in PDF */}
            {(() => {
              // Section notes count as notes here too — a test with section
              // notes and no Insights note still belongs in this block.
              const anyHasNotes = bloodworkAnalysisHistory.some(bwa =>
                comparisonNotesTextFor(bwa),
              );
              if (!this.state.withNotes || !anyHasNotes)
                return null;
              const notesTable = this.practitionerNotesTable(
                bloodworkAnalysisHistory,
                true,
              );
              return notesTable;
            })()}

            {/* Overall Practitioner Notes in PDF */}
            {(() => {
              if (!this.state.withNotes) return null;
              const noteText = actualClient?.overallPractitionerNotes || '';
              if (!noteText.trim()) return null;

              // Use the same pagination mechanism (see paginateNoteRows for
              // where the page geometry comes from: ~744 CSS px per page, and
              // why the safety margin sits here rather than in WRAP_CHARS).
              const MAX_PAGE_HEIGHT = 700;
              // Top spacer (TOP_OF_PAGE_SPACER) + card header + offsets.
              const HEADER_HEIGHT = 76;
              const ROW_PADDING = 12;
              const LINE_HEIGHT = 18;

              const lines = noteText.split('\n');
              const overallChunks: string[][] = [];
              let currentLines: string[] = [];
              let currentChunkHeight = HEADER_HEIGHT;

              lines.forEach(line => {
                // This box is the full width of a LANDSCAPE page: ~1063px of
                // text over a ~5.6px average character width, so ~189
                // characters on one rendered line. The REAL figure, no cushion
                // — counting a 160-char bullet as two lines (the old /120)
                // inflated the estimate by ~2x and broke the panel a page
                // early. The margin is in MAX_PAGE_HEIGHT instead.
                const WRAP_CHARS = 189;
                const wrapCount = Math.max(1, Math.ceil(line.length / WRAP_CHARS));
                const lineH = wrapCount * LINE_HEIGHT;

                if (
                  currentChunkHeight + lineH > MAX_PAGE_HEIGHT &&
                  currentLines.length > 0
                ) {
                  overallChunks.push(currentLines);
                  currentLines = [line];
                  currentChunkHeight = HEADER_HEIGHT + lineH;
                } else {
                  currentLines.push(line);
                  currentChunkHeight += lineH;
                }
              });
              if (currentLines.length > 0) overallChunks.push(currentLines);

              return overallChunks.map((linesChunk, idx) => (
                <div
                  key={`overall-chunk-${idx}`}
                  className="pdf-section"
                  style={{ pageBreakInside: 'auto' }}
                >
                  <div className="html2pdf__page-break" />
                  <div style={{ height: TOP_OF_PAGE_SPACER }} />
                  {/* Blue header + outline, matching the Disclaimer and the
                      Insights card. A plain Card, not "disclaimer-panel" \u2014
                      that class is in html2pdf's break-inside:avoid list and
                      would stop these chunks paginating. */}
                  <div className="section-block">
                    <Card style={{ marginTop: 0 }}>
                      <Card.Header className="py-1">
                        Overall Progress &amp; Trends{idx > 0 ? ' (cont.)' : ''}
                      </Card.Header>
                      <Card.Body
                        style={{
                          padding: '10px',
                          wordBreak: 'break-word',
                          minHeight: '2em',
                          color: NOTE_TEXT_COLOR,
                        }}
                      >
                        {linesChunk.map((line, lIdx, lArr) => (
                          <div
                            key={lIdx}
                            style={{
                              pageBreakInside: 'avoid',
                              whiteSpace: 'pre-wrap',
                            }}
                          >
                            {/* Formatted, like the on-screen field \u2014 the
                                practitioner's **bold** must not print as markup.
                                Still one div per line so the chunker's page math
                                is unchanged. */}
                            {renderNoteLine(line)}
                          </div>
                        ))}
                      </Card.Body>
                    </Card>
                  </div>
                  <div style={{ height: '12px' }} />
                </div>
              ));
            })()}
            {/* Disclaimer */}
            {/* No explicit html2pdf__page-break here: .disclaimer-panel is in
                html2pdf's `avoid` list (see PDFModal's pagebreak opts), so the
                panel is already pushed whole to a fresh page if it would straddle
                a boundary. A forced break ON TOP of that risks html2pdf's
                phantom-blank-page bug — when the preceding content ends exactly
                at a page edge, the forced "after" break expands its fill-pad to a
                full blank page, leaving the disclaimer page looking empty. Letting
                the keep-together rule place it avoids that entirely.

                The wrapper's padding-top is what stops the blue header being
                shaved onto the previous page. html2pdf's "avoid" pad lands the
                element on the page boundary EXACTLY (pagebreaks.js:97-100),
                and the canvas is then sliced on a grid rounded down to whole
                device pixels (worker.js:181) — so a boundary-hugging top edge
                loses a pixel or two to the page above. Padding INSIDE the
                pushed element gives those pixels something blank to eat.
                Mirrors .pdf-keep-together-block in containers/pdf.css. */}
            <div className="pdf-disclaimer-block">
              <DisclaimerSection furtherConsiderations={furtherConsiderations} />
            </div>
          </div>
        </PDFModal>

        {showPdfModal ? null : (
          <>
            <Card id="blood-work-comparison" ref={this.comparisonCardRef as any}>
              <Card.Header>
                Blood Work Comparison&nbsp;
                <span
                  className="float-right d-flex align-items-center"
                  style={{ gap: '16px' }}
                >
                  {/* Export Raw Data is LIVE in all envs. The AI "Generate with
                      AI" summary below stays localhost-only (see gates further down). */}
                  {(
                    <>
                      <span
                        className="d-flex align-items-center"
                        style={{ gap: '14px' }}
                      >
                        <span>Export All Client Data</span>
                        <OverlayTrigger
                          trigger="click"
                          rootClose
                          overlay={this.exportPromptPopover()}
                          placement="bottom"
                        >
                          <FontAwesomeIcon
                            icon={faInfoCircle}
                            style={{
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '18px',
                            }}
                          />
                        </OverlayTrigger>
                        <OverlayTrigger
                          overlay={this.exportJsonTooltip()}
                          placement="bottom"
                        >
                          <FontAwesomeIcon
                            icon={faFileCode}
                            onClick={this.exportJson}
                            style={{
                              color: 'white',
                              cursor: 'pointer',
                              fontSize: '18px',
                            }}
                          />
                        </OverlayTrigger>
                      </span>
                      {this.headerDivider()}
                    </>
                  )}
                  <span
                    className="d-flex align-items-center"
                    style={{ gap: '14px' }}
                  >
                    <label
                      htmlFor="matchAnalysisOrderToggle"
                      style={{
                        color: 'white',
                        marginBottom: '0',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      Match Analysis Order
                    </label>
                    <OverlayTrigger
                      overlay={this.matchAnalysisOrderTooltip()}
                      placement="bottom"
                    >
                      <FontAwesomeIcon
                        icon="info-circle"
                        style={{ cursor: 'help', fontSize: '18px' }}
                      />
                    </OverlayTrigger>
                    <input
                      id="matchAnalysisOrderToggle"
                      className="lsm-styled-checkbox"
                      type="checkbox"
                      checked={this.state.matchAnalysisOrder}
                      onChange={e =>
                        this.handleMatchAnalysisOrderChange(e.target.checked)
                      }
                    />
                  </span>
                  {this.headerDivider()}
                  <span
                    className="d-flex align-items-center"
                    style={{ gap: '14px' }}
                  >
                    <label
                      htmlFor="hideEmptyRowsToggle"
                      style={{
                        color: 'white',
                        marginBottom: '0',
                        fontWeight: 500,
                        cursor: 'pointer',
                      }}
                    >
                      Hide Blank Rows
                    </label>
                    <OverlayTrigger
                      overlay={this.hideBlankRowsTooltip()}
                      placement="bottom"
                    >
                      <FontAwesomeIcon
                        icon="info-circle"
                        style={{ cursor: 'help', fontSize: '18px' }}
                      />
                    </OverlayTrigger>
                    <input
                      id="hideEmptyRowsToggle"
                      type="checkbox"
                      checked={!this.state.showHiddenRows}
                      onChange={e =>
                        this.setState(
                          { showHiddenRows: !e.target.checked },
                          this.scrollComparisonTitleIntoView,
                        )
                      }
                      style={{
                        width: '18px',
                        height: '18px',
                        cursor: 'pointer',
                        appearance: 'none',
                        WebkitAppearance: 'none',
                        MozAppearance: 'none',
                        backgroundColor: 'white', // white background box
                        border: '1px solid white', // blends with header
                        borderRadius: '2px',
                        position: 'relative',
                      }}
                    />
                  </span>
                  {this.headerDivider()}
                  <span
                    className="d-flex align-items-center"
                    style={{ gap: '14px' }}
                  >
                    <span style={{ color: 'white', fontWeight: 500 }}>
                      Export to PDF
                    </span>
                    <OverlayTrigger
                      overlay={this.exportInfoTooltip()}
                      placement="bottom"
                    >
                      <FontAwesomeIcon
                        icon="info-circle"
                        onClick={this.openExportInfoWindow}
                        style={{ cursor: 'pointer', fontSize: '18px' }}
                      />
                    </OverlayTrigger>
                    <OverlayTrigger
                      overlay={this.exportTooltip()}
                      placement="bottom"
                    >
                      <FontAwesomeIcon
                        icon={faFileDownload}
                        onClick={() => this.export(false)}
                        style={{ cursor: 'pointer', fontSize: '18px' }}
                      />
                    </OverlayTrigger>
                    <OverlayTrigger
                      overlay={this.exportWithNotesTooltip()}
                      placement="bottom"
                    >
                      <FontAwesomeIcon
                        icon={faFileDownloadSolid}
                        onClick={() => this.export(true)}
                        style={{ cursor: 'pointer', fontSize: '18px' }}
                      />
                    </OverlayTrigger>
                  </span>
                </span>
              </Card.Header>
              <Card.Body>
                {/* Sections render in the order chosen by the "Match Analysis
                    Order" toggle (see comparisonSectionOrder). */}
                {this.buildComparisonSections(
                  bloodworkAnalysisHistory,
                  actualClient.gender,
                  whiteBloodCellsHasValues,
                ).map(section => (
                  <React.Fragment key={section.key}>
                    <div style={{ height: '8px' }} />
                    <h5>
                      <strong>{section.title}</strong>
                    </h5>
                    {section.table}
                    <div style={{ height: '8px' }} />
                  </React.Fragment>
                ))}

                {/* Insights note OR section notes — either one earns the block. */}
                {(() => {
                  const anyNotes = bloodworkAnalysisHistory.some(bwa =>
                    comparisonNotesTextFor(bwa),
                  );
                  if (!anyNotes) return null;
                  // The toggle only appears when there are section notes to
                  // show, and the title follows what the table is actually
                  // rendering (same rule as practitionerNotesTable).
                  const anySectionNotes = bloodworkAnalysisHistory.some(bwa =>
                    sectionNotesTextFor(bwa),
                  );
                  const showingSectionNotes =
                    this.state.showSectionNotes && anySectionNotes;
                  return (
                    // Blue header + outline, so this reads as the same kind of
                    // panel as Overall Progress & Trends and the Disclaimer.
                    <Card className="mt-4">
                      <Card.Header className="d-flex align-items-center justify-content-between">
                        <span>
                          {showingSectionNotes
                            ? 'Insights & Section Notes by Collection Date'
                            : 'Insights by Collection Date'}
                        </span>
                        {anySectionNotes && (
                          <span
                            className="d-flex align-items-center"
                            style={{ gap: '10px' }}
                          >
                            <label
                              htmlFor="showSectionNotesToggle"
                              style={{
                                marginBottom: 0,
                                fontWeight: 500,
                                cursor: 'pointer',
                              }}
                            >
                              Show Section Notes
                            </label>
                            <OverlayTrigger
                              overlay={this.showSectionNotesTooltip()}
                              placement="left"
                            >
                              <FontAwesomeIcon
                                icon="info-circle"
                                style={{ cursor: 'help', fontSize: '16px' }}
                              />
                            </OverlayTrigger>
                            {/* lsm-styled-checkbox carries the white-on-blue box
                                AND the :checked checkmark (App.css) — a plain
                                inline-styled box would render blank when on. */}
                            <input
                              id="showSectionNotesToggle"
                              className="lsm-styled-checkbox"
                              type="checkbox"
                              checked={this.state.showSectionNotes}
                              onChange={e =>
                                this.handleShowSectionNotesChange(
                                  e.target.checked,
                                )
                              }
                            />
                          </span>
                        )}
                      </Card.Header>
                      <Card.Body className="p-0">
                        {this.practitionerNotesTable(
                          bloodworkAnalysisHistory,
                          false,
                        )}
                      </Card.Body>
                    </Card>
                  );
                })()}

                {/* id anchors Save's scroll-back to the top of this card. */}
                <Card className="mt-4" id="overall-progress">
                  <Card.Header className="d-flex align-items-center justify-content-between">
                    <span>Overall Progress & Trends</span>
                    {process.env.REACT_APP_ENV !== 'prod' &&
                      process.env.REACT_APP_ENV !== 'slava' && (
                        <Button
                          size="sm"
                          variant="light"
                          onClick={this.onGenerateSummary}
                          disabled={this.state.generatingSummary}
                        >
                          {this.state.generatingSummary
                            ? 'Generating…'
                            : 'Generate with AI'}
                        </Button>
                      )}
                  </Card.Header>
                  <Card.Body>
                    {process.env.REACT_APP_ENV !== 'prod' &&
                      process.env.REACT_APP_ENV !== 'slava' && (
                        <div
                          style={{
                            fontSize: '12px',
                            color: '#a0522d',
                            fontWeight: 600,
                            marginBottom: '6px',
                          }}
                        >
                          ⚠️ AI-generated draft. This text appears on the client's
                          report — please review and edit it before saving and
                          printing.
                        </div>
                      )}
                    <ClientNotes
                      title="Add your own notes here. Click Save when finished."
                      client={actualClient}
                      clientEntryStore={this.props.clientEntryStore}
                      aiDraft={this.state.aiSummaryDraft}
                      scrollAnchorId="overall-progress"
                    />
                  </Card.Body>
                </Card>

              {/* Disclaimer in UI */}
              <div className="mt-4">
                <DisclaimerSection furtherConsiderations={furtherConsiderations} />
              </div>

              </Card.Body>
            </Card>



            <GenericModal
              show={showUpgradeModal}
              title="Export to PDF Feature Not Available"
              text="Export to PDF feature is not available with a free account."
              link="https://labsmarts.com/pricing"
              linkText="Click here to upgrade."
              onHide={() => this.setState({ showUpgradeModal: false })}
            />
          </>
        )}
      </>
    );
  }
}
